import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
  Divider,
  Avatar,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import OpacityIcon from '@mui/icons-material/Opacity';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { format } from 'date-fns';


const KIOSK_REPORTS_API_URL = (process.env.REACT_APP_KIOSK_REPORTS_API_URL as string | undefined) || 'https://api.profile.ayurai.in/getKioskReportsByDateRange';

// Normalized kiosk report row structure derived from the provided sample payload
interface KioskReport {
  id: string | number;
  date: string;
  email?: string;
  uid?: string; // from nested JSON (e.g., vikriti.UID or Wellness.UID)
  rbs?: string; // e.g., "92 mg/dL"
  wellnessFocus?: string[]; // selected Wellness category names
  reportUrl?: string; // Data.Report
  kioskId?: string; // optional kiosk identifier
  kioskName?: string; // optional kiosk display name
  raw: any; // full raw object for details dialog
}

const KioskReportsPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [rowSelection, setRowSelection] = useState<GridRowSelectionModel>([]);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [visibleFields, setVisibleFields] = useState<string[] | null>(null);
  // Report dialog state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [bcaOpen, setBcaOpen] = useState(false);
  const [bcaData, setBcaData] = useState<any>(null);
  const [prakritiOpen, setPrakritiOpen] = useState(false);
  const [prakritiData, setPrakritiData] = useState<any>(null);
  const [wellnessOpen, setWellnessOpen] = useState(false);
  const [wellnessData, setWellnessData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'reports' | 'analytics'>('reports');
  const [detailTab, setDetailTab] = useState<'profile' | 'devices' | 'vikriti' | 'prakriti' | 'wellness' | 'bca' | 'report'>('profile');
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthDialogHtml, setHealthDialogHtml] = useState<string | null>(null);

  // Refs for scroll synchronization
  const scrollIndicatorRef = React.useRef<HTMLDivElement>(null);
  const dataGridContainerRef = React.useRef<HTMLDivElement>(null);

  // Open BCA dialog with parsed content
  const openBcaDialog = (rawVal: any) => {
    let parsed: any = rawVal;
    if (typeof rawVal === 'string') {
      try {
        parsed = JSON.parse(rawVal);
      } catch {
        parsed = rawVal; // keep as string if not JSON
      }
    }
    setBcaData(parsed);
    setBcaOpen(true);
  };

  // Pretty render Wellness content inside dialog
  const renderPrettyWellness = (val: any): React.ReactNode => {
    if (val == null) return <Typography variant="body2">No data</Typography>;
    let data: any = val;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { /* keep as string */ }
    }
    if (typeof data === 'string') {
      return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{data}</pre>;
    }
    const uid = data?.UID ?? data?.uid;
    const categories: any[] = Array.isArray(data?.Categories) ? data.Categories : [];
    if (!categories.length) return <Typography variant="body2">No categories</Typography>;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {uid && <Chip size="small" label={`UID: ${uid}`} />}
          <Chip size="small" color="primary" label={`Selected: ${categories.filter(c => c?.selected).length}`} />
          <Chip size="small" label={`Total: ${categories.length}`} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 2 }}>
          {categories.map((c, idx) => (
            <Card key={idx} variant="outlined" sx={{ overflow: 'hidden' }}>
              <CardContent sx={{ display: 'flex', gap: 2 }}>
                <img src={c?.imageUrl} alt={c?.name} style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: c?.selected ? '2px solid #1c8686' : '1px solid #ddd' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{c?.name}</Typography>
                  {c?.description ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.description}</Typography>
                  ) : null}
                  {c?.selected ? <Chip size="small" color="success" label="Selected" sx={{ mt: 0.5 }} /> : null}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  };

  const openWellnessDialog = (rawVal: any) => {
    let parsed: any = rawVal;
    if (typeof rawVal === 'string') {
      try { parsed = JSON.parse(rawVal); } catch { parsed = rawVal; }
    }
    setWellnessData(parsed);
    setWellnessOpen(true);
  };

  const getSelectedWellnessNames = (value: any): string => {
    let val = value;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { return '-'; }
    }
    const categories: any[] = Array.isArray(val?.Categories) ? val.Categories : [];
    const selectedCats = categories.filter((c: any) => c?.selected);
    if (selectedCats.length === 0) return '-';
    return selectedCats.map((c: any) => c?.name).filter(Boolean).join(', ');
  };

  const renderWellnessIcons = (value: any, onClick: (e: React.MouseEvent) => void) => {
    let val = value;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { }
    }
    const categories: any[] = Array.isArray(val?.Categories) ? val.Categories : [];
    const selectedCats = categories.filter((c: any) => c?.selected);
    if (!categories.length) return <Typography variant="body2">-</Typography>;
    if (selectedCats.length === 0) {
      return (
        <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
          <Chip size="small" label="None selected" variant="outlined" />
        </Box>
      );
    }
    const firstFew = selectedCats.slice(0, 4);
    return (
      <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
        {firstFew.map((c, idx) => (
          <img key={idx} src={c?.imageUrl} alt={c?.name} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} />
        ))}
        {selectedCats.length > firstFew.length ? (
          <Chip size="small" label={`+${selectedCats.length - firstFew.length}`} variant="outlined" />
        ) : null}
      </Box>
    );
  };

  const renderPrettyBca = (val: any): React.ReactNode => {
    if (val == null) return <Typography variant="body2">No data</Typography>;

    if (typeof val === 'string') {
      const isUrl = /^https?:\/\//i.test(val);
      const isPdf = isUrl && val.toLowerCase().includes('.pdf');
      if (isUrl) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button size="small" variant="contained" onClick={() => window.open(val, '_blank')}>Open Report</Button>
            {isPdf ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', height: 520 }}>
                <iframe title="BCA Report" src={val} style={{ width: '100%', height: '100%', border: 0 }} />
              </Box>
            ) : null}
            {!isPdf ? (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{val}</pre>
            ) : null}
          </Box>
        );
      }
      try {
        const parsed = JSON.parse(val);
        return renderPrettyBca(parsed);
      } catch {
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{val}</pre>;
      }
    }

    // Try specialized BCA layout if we detect known structure
    const bodyInfo = (val?.next?.value?.bodyInfo) || (val?.bodyInfo);
    if (bodyInfo && typeof bodyInfo === 'object') {
      const safe = (v: any) => (v !== undefined && v !== null && v !== '' ? v : '--');
      const num = (v: any, d = 2) => (typeof v === 'number' ? v.toFixed(d) : (v ? String(v) : '--'));
      const mapBodyType = (t: any) => {
        switch (t) {
          case 1: return 'Unknown';
          case 2: return 'Slightly Fat';
          case 3: return 'Obese';
          case 4: return 'Slim';
          case 5: return 'Standard';
          case 6: return 'Strong';
          case 7: return 'Skinny';
          case 8: return 'Dynamic';
          case 9: return 'Bodybuilding';
          default: return '--';
        }
      };

      const Section: React.FC<{ title: string; items: Array<[string, React.ReactNode]> }> = ({ title, items }) => (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#f9f9f9', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, color: '#1c8686', fontWeight: 600 }}>{title}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 1.5 }}>
            {items.map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: 1, p: 1.25, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      );

      return (
        <Box sx={{ fontFamily: 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, Arial', lineHeight: 1.4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>BCA Report</Typography>
          <Section
            title="Basic Info"
            items={[
              ['Body Mass Index (BMI)', num(bodyInfo?.bhBMI)],
              ['Body Score', num(bodyInfo?.bhBodyScore)],
              ['Body Age', safe(bodyInfo?.bhBodyAge)],
              ['Body Type', mapBodyType(bodyInfo?.bhBodyType)],
            ]}
          />
          <Section
            title="Fat Composition"
            items={[
              ['Body Fat Mass (kg)', num(bodyInfo?.bhBodyFatKg)],
              ['Body Fat Percentage (%)', num(bodyInfo?.bhBodyFatRate)],
              ['Fat-Free Mass (kg)', num(bodyInfo?.bhBodyFatFreeMassKg)],
              ['Subcutaneous Fat (kg)', num(bodyInfo?.bhBodyFatSubCutKg)],
              ['Subcutaneous Fat Rate (%)', num(bodyInfo?.bhBodyFatSubCutRate)],
              ['Visceral Fat Level', safe(bodyInfo?.bhVFAL)],
            ]}
          />
          <Section
            title="Muscle & Bone"
            items={[
              ['Muscle Mass (kg)', num(bodyInfo?.bhMuscleKg)],
              ['Muscle Rate (%)', num(bodyInfo?.bhMuscleRate)],
              ['Skeletal Muscle (kg)', num(bodyInfo?.bhSkeletalMuscleKg)],
              ['Skeletal Muscle Rate (%)', num(bodyInfo?.bhSkeletalMuscleRate)],
              ['Bone Mass (kg)', num(bodyInfo?.bhBoneKg)],
              ['Bone Rate (%)', num(bodyInfo?.bhBoneRate)],
              ['Muscle Storage Level', safe(bodyInfo?.bhMuscleStorageLevel)],
            ]}
          />
          <Section
            title="Water & Protein"
            items={[
              ['Water Mass (kg)', num(bodyInfo?.bhWaterKg)],
              ['Water Rate (%)', num(bodyInfo?.bhWaterRate)],
              ['Protein Mass (kg)', num(bodyInfo?.bhProteinKg)],
              ['Protein Rate (%)', num(bodyInfo?.bhProteinRate)],
            ]}
          />
          <Section
            title="Metabolism & Other"
            items={[
              ['Basal Metabolic Rate (kcal)', num(bodyInfo?.bhBMR)],
              ['Active Metabolic Rate (kcal)', num(bodyInfo?.bhAMR)],
              ['Cardiac Index', num(bodyInfo?.bhCI)],
              ['Mineral Content (kg)', num(bodyInfo?.bhMineralKg)],
              ['Body Impedance', num(bodyInfo?.resistance)],
            ]}
          />
        </Box>
      );
    }

    // Generic pretty-print fallback for arrays/objects
    if (Array.isArray(val)) {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {val.map((item, idx) => (
            <Card key={idx} variant="outlined">
              <CardContent>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(item, null, 2)}</pre>
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }
    if (typeof val === 'object') {
      const entries = Object.entries(val as Record<string, any>);
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {entries.map(([k, v]) => (
            <Card key={k} variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{k}</Typography>
                <Typography variant="body2" component="div">
                  {typeof v === 'object' ? (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(v, null, 2)}</pre>
                  ) : String(v)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }
    return <Typography variant="body2">{String(val)}</Typography>;
  };

  const openPrakritiDialog = (rawVal: any) => {
    let parsed: any = rawVal;
    if (typeof rawVal === 'string') {
      try {
        parsed = JSON.parse(rawVal);
      } catch {
        parsed = rawVal;
      }
    }
    setPrakritiData(parsed);
    setPrakritiOpen(true);
  };

  const openReportDialog = (rawVal: any) => {
    const pickUrl = (v: any): string | null => {
      const isHttp = (s: any) => typeof s === 'string' && /^https?:\/\//i.test(s);
      if (isHttp(v)) return v as string;
      if (v && typeof v === 'object') {
        const candidates = [
          v.url, v.URL, v.link, v.href, v.report, v.Report,
        ];
        for (const c of candidates) {
          if (isHttp(c)) return c as string;
        }
      }
      return null;
    };

    let val: any = rawVal;
    if (typeof rawVal === 'string') {
      try { val = JSON.parse(rawVal); } catch { val = rawVal; }
    }
    // Try direct, then nested common containers
    const url =
      pickUrl(val) ||
      pickUrl(val?.data) || pickUrl(val?.Data) ||
      pickUrl(val?.report) || pickUrl(val?.Report);

    setReportUrl(url);
    setReportOpen(true);
  };

  const renderPrettyPrakriti = (val: any): React.ReactNode => {
    if (val == null) return <Typography variant="body2">No data</Typography>;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return renderPrettyPrakriti(parsed);
      } catch {
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{val}</pre>;
      }
    }

    const fmtVpk = (vpk: any) => {
      if (!vpk) return '--';
      if (typeof vpk === 'string') return vpk;
      if (typeof vpk === 'object') {
        const v = vpk.v ?? vpk.vata ?? vpk.V ?? vpk.VATA;
        const p = vpk.p ?? vpk.pitta ?? vpk.P ?? vpk.PITTA;
        const k = vpk.k ?? vpk.kapha ?? vpk.K ?? vpk.KAPHA;
        const parts = [
          v !== undefined ? `V:${v}` : null,
          p !== undefined ? `P:${p}` : null,
          k !== undefined ? `K:${k}` : null,
        ].filter(Boolean);
        return parts.length ? parts.join('  ') : JSON.stringify(vpk);
      }
      return String(vpk);
    };

    type Option = { label: string; isSelected?: boolean; score?: number | string; vpk?: any };
    type QA = { question: string; id?: string; qid?: string; selected?: { label: string; score?: number | string; vpk?: any }; options?: Option[] };
    const extractQA = (input: any): QA[] => {
      const out: QA[] = [];
      const coerceLabel = (opt: any) => opt?.label ?? opt?.text ?? opt?.answer ?? opt?.Answer ?? opt?.value ?? String(opt);
      const getSelected = (opts: any[]): QA['selected'] | undefined => {
        const sel = opts.find((o) => o?.isSelected === true || o?.IsSelected === true) ?? opts.find((o) => o?.selected === true);
        if (!sel) return undefined;
        return { label: coerceLabel(sel), score: sel?.score ?? sel?.points ?? sel?.Score ?? sel?.Points, vpk: sel?.vpk ?? sel?.VPK };
      };
      const mapOptions = (opts: any[] | undefined): Option[] | undefined => {
        if (!Array.isArray(opts)) return undefined;
        return opts.map((o) => ({
          label: coerceLabel(o),
          isSelected: o?.isSelected === true || o?.IsSelected === true || o?.selected === true,
          score: o?.score ?? o?.points ?? o?.Score ?? o?.Points,
          vpk: o?.vpk ?? o?.VPK,
        }));
      };

      if (Array.isArray(input)) {
        input.forEach((it) => {
          const q = it?.question ?? it?.Question ?? it?.q ?? (typeof it === 'string' ? it : JSON.stringify(it));
          const options = it?.answers ?? it?.Answers ?? it?.options ?? it?.choices ?? it?.Options ?? [];
          const selected = Array.isArray(options) ? getSelected(options) : undefined;
          if (q) out.push({ question: String(q), id: it?.Id ?? it?.id, qid: it?.qid ?? it?.QID, selected, options: mapOptions(options) });
        });
        return out;
      }
      if (Array.isArray(input?.questions)) {
        input.questions.forEach((it: any) => {
          const q = it?.question ?? it?.Question ?? it?.q ?? JSON.stringify(it);
          const options = it?.answers ?? it?.Answers ?? it?.options ?? it?.choices ?? [];
          const selected = Array.isArray(options) ? getSelected(options) : undefined;
          out.push({ question: String(q), id: it?.Id ?? it?.id, qid: it?.qid ?? it?.QID, selected, options: mapOptions(options) });
        });
        return out;
      }
      if (typeof input === 'object' && input) {
        // Map object fallback: keys as questions, value could be selected label
        Object.entries(input as Record<string, any>).forEach(([k, v]) => {
          if (Array.isArray(v)) {
            const qa = extractQA([{ question: k, answers: v }])[0];
            out.push({ question: k, id: qa?.id, qid: qa?.qid, selected: qa?.selected, options: qa?.options });
          } else if (typeof v === 'object' && v) {
            const options = (v as any).answers ?? (v as any).options ?? (v as any).choices;
            const qa = Array.isArray(options) ? extractQA([{ question: k, answers: options }])[0] : undefined;
            out.push({ question: k, id: qa?.id, qid: qa?.qid, selected: qa?.selected ?? { label: typeof v === 'string' ? v : JSON.stringify(v) }, options: qa?.options });
          } else {
            out.push({ question: k, selected: { label: String(v) } });
          }
        });
        return out;
      }
      return out;
    };

    // If the incoming value is a JSON string, parse it so we can read root fields
    const parsedVal = (() => {
      if (typeof val === 'string') {
        try { return JSON.parse(val); } catch { return val; }
      }
      return val;
    })();

    // Use parsedVal directly as the root prakriti object
    const parsedRoot = parsedVal as any;
    console.log('[PrakritiDialog] parsedRoot:', parsedRoot);

    // Prefer PrakritiCollection (array of Q/A) if present; else fall back to the full value
    const qaSource = (parsedVal as any)?.PrakritiCollection ?? parsedVal;
    const qas = extractQA(qaSource);
    const hasAny = qas.length > 0 && qas.some(q => q.question);

    // Compute totals by selected answers' VPK tags (supports combos like 'vk')
    const vpkTotals = { v: 0, p: 0, k: 0 } as Record<'v' | 'p' | 'k', number>;
    const vpkCounts = { v: 0, p: 0, k: 0 } as Record<'v' | 'p' | 'k', number>;
    qas.forEach(q => {
      const code = (q.selected?.vpk ?? '').toString().toLowerCase();
      if (!code) return;
      const score = parseFloat(String(q.selected?.score ?? '0')) || 0;
      const add = (c: 'v' | 'p' | 'k') => { vpkTotals[c] += score; vpkCounts[c] += 1; };
      if (code.includes('v')) add('v');
      if (code.includes('p')) add('p');
      if (code.includes('k')) add('k');
    });

    // Extract root-level date and uid if present (use parsed root)
    const rawDate = (parsedRoot?.date ?? parsedRoot?.Date ?? parsedRoot?.createdAt ?? parsedRoot?.CreatedAt ?? parsedRoot?.timestamp ?? parsedRoot?.Timestamp) as any;
    let dateLabel: string | undefined;
    if (rawDate) {
      // Support numeric epoch strings
      const epochLike = typeof rawDate === 'string' && /^\d+$/.test(rawDate) ? Number(rawDate) : rawDate;
      const d = new Date(epochLike as any);
      dateLabel = isNaN(d.getTime()) ? String(rawDate) : format(d, 'yyyy-MM-dd HH:mm');
    }
    const uid = (parsedRoot?.uid ?? parsedRoot?.UID ?? parsedRoot?.userId ?? parsedRoot?.UserId ?? parsedRoot?.customerId ?? parsedRoot?.CustomerId) as any;

    // Extract requested root-level prakriti summary fields
    const rootVata = (parsedRoot?.vata ?? parsedRoot?.Vata) as number | string | undefined;
    const rootPitta = (parsedRoot?.pitta ?? parsedRoot?.Pitta) as number | string | undefined;
    const rootKapha = (parsedRoot?.kapha ?? parsedRoot?.Kapha) as number | string | undefined;
    const rootRawVata = (parsedRoot?.rawVata ?? parsedRoot?.RawVata) as number | string | undefined;
    const rootRawPitta = (parsedRoot?.rawPitta ?? parsedRoot?.RawPitta) as number | string | undefined;
    const rootRawKapha = (parsedRoot?.rawKapha ?? parsedRoot?.RawKapha) as number | string | undefined;
    const rootResult = (parsedRoot?.result ?? parsedRoot?.Result) as string | undefined;
    const rootId = (parsedRoot?.id ?? parsedRoot?.Id ?? parsedRoot?.ID) as string | undefined;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Top: Vata / Pitta / Kapha totals and IDs info */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Root-level summary from prakriti object */}
          {rootVata !== undefined && <Chip label={`Vata: ${Number(rootVata).toFixed ? Number(rootVata).toFixed(2) : rootVata}`} size="small" color="primary" />}
          {rootPitta !== undefined && <Chip label={`Pitta: ${Number(rootPitta).toFixed ? Number(rootPitta).toFixed(2) : rootPitta}`} size="small" color="warning" />}
          {rootKapha !== undefined && <Chip label={`Kapha: ${Number(rootKapha).toFixed ? Number(rootKapha).toFixed(2) : rootKapha}`} size="small" color="success" />}
          {rootRawVata !== undefined && <Chip label={`Raw Vata: ${rootRawVata}`} size="small" variant="outlined" />}
          {rootRawPitta !== undefined && <Chip label={`Raw Pitta: ${rootRawPitta}`} size="small" variant="outlined" />}
          {rootRawKapha !== undefined && <Chip label={`Raw Kapha: ${rootRawKapha}`} size="small" variant="outlined" />}
          {rootResult && <Chip label={`Result: ${rootResult}`} size="small" variant="outlined" />}
          {/* Keep identifiers and date if present */}
          {rootId && <Chip label={`ID: ${rootId}`} size="small" variant="outlined" />}
          {uid && !rootId && <Chip label={`UID: ${uid}`} size="small" variant="outlined" />}
          {dateLabel && <Chip label={`Date: ${dateLabel}`} size="small" variant="outlined" />}
        </Box>
        {/* Only: full answer list per question */}
        {qas.map((qa, idx) => (
          <Box key={`list-${qa.question}-${idx}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{qa.question}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {(qa.options ?? []).map((op, i) => (
                <Box key={`op-${i}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: op.isSelected ? 'primary.main' : 'divider', bgcolor: op.isSelected ? 'primary.main' : 'transparent', color: op.isSelected ? 'primary.contrastText' : 'text.primary' }}>
                  <Typography variant="caption" sx={{ fontWeight: op.isSelected ? 700 : 400 }}>{op.label}</Typography>
                  {op.score !== undefined && (
                    <Typography variant="caption">• Score: {op.score}</Typography>
                  )}
                  {op.vpk !== undefined && (
                    <Typography variant="caption">• VPK: {fmtVpk(op.vpk)}</Typography>
                  )}
                  {op.isSelected && (
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>• Selected</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const fetchReports = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      if (!KIOSK_REPORTS_API_URL) {
        setError('Kiosk Reports API URL is not configured. Set REACT_APP_KIOSK_REPORTS_API_URL in .env');
        setLoading(false);
        return;
      }

      const body: any = {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      };


      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 1 minute timeout

      const res = await fetch(KIOSK_REPORTS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        redirect: 'follow',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Accept either {reports: [...]} or a raw array
      const text = await res.text();
      const json = text ? JSON.parse(text) : {};
      const list: any[] = Array.isArray(json) ? json : (json.reports || json.data || []);

      // Add a stable id for DataGrid and precompute health score per row
      const withIdsAndHealth = (list || []).map((r, idx) => {
        const base: any = { ...r, id: r.id ?? r.kioskId ?? idx };
        try {
          const d = (r?.data ?? r?.Data) as any || {};
          const profile = (r?.profileData ?? r?.ProfileData) as any;
          const vikriti = d?.vikriti ?? d?.Vikriti;
          const systolic = d?.systolic;
          const diastolic = d?.diastolic;
          const spo2 = d?.spo2;
          const pulse = d?.pulse;
          const rbs = d?.rbs;
          const handgripStrength = d?.handgrip ?? d?.handgripStrength ?? d?.handGripStrength;
          const hs = calculateKioskHealthScore({ vikriti, systolic, diastolic, spo2, pulse, rbs, profile, handgripStrength });
          if (hs && typeof hs.healthIndex === 'number') {
            base.healthIndex = hs.healthIndex;
          }
          if (hs && typeof hs.status === 'string') {
            base.healthStatus = hs.status;
          }
        } catch {
          // ignore health score errors during enrichment
        }
        return base;
      });

      setReports(withIdsAndHealth);
    } catch (e: any) {
      console.error('Error fetching kiosk reports:', e);
      setError(e?.message || 'Failed to fetch kiosk reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Sync scroll between DataGrid and custom scroll indicator
  useEffect(() => {
    const syncScroll = () => {
      const dataGridContainer = dataGridContainerRef.current;
      const scrollIndicator = scrollIndicatorRef.current;

      if (!dataGridContainer || !scrollIndicator) return;

      // Find the DataGrid's virtual scroller
      const virtualScroller = dataGridContainer.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
      if (!virtualScroller) return;

      // Sync scroll from DataGrid to indicator
      const handleDataGridScroll = () => {
        if (scrollIndicator) {
          scrollIndicator.scrollLeft = virtualScroller.scrollLeft;
        }
      };

      // Sync scroll from indicator to DataGrid
      const handleIndicatorScroll = () => {
        if (virtualScroller) {
          virtualScroller.scrollLeft = scrollIndicator.scrollLeft;
        }
      };

      virtualScroller.addEventListener('scroll', handleDataGridScroll);
      scrollIndicator.addEventListener('scroll', handleIndicatorScroll);

      return () => {
        virtualScroller.removeEventListener('scroll', handleDataGridScroll);
        scrollIndicator.removeEventListener('scroll', handleIndicatorScroll);
      };
    };

    // Set up sync after a small delay to ensure DataGrid is rendered
    const timer = setTimeout(syncScroll, 100);
    return () => clearTimeout(timer);
  }, [reports, activeTab]);

  // Filter reports based on search query
  const searchFilteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;

    const query = searchQuery.toLowerCase().trim();
    return reports.filter((report) => {
      const profileData = report?.profileData || report?.ProfileData || {};
      const uid = (report?.uid || report?.UID || profileData?.UID || profileData?.uid || '').toString().toLowerCase();
      const firstname = (profileData?.firstname || profileData?.firstName || '').toString().toLowerCase();
      const email = (profileData?.email || '').toString().toLowerCase();
      const phone = (
        profileData?.phone ||
        profileData?.phoneNumber ||
        profileData?.mobile ||
        profileData?.mobileNumber ||
        profileData?.contactNumber ||
        report?.phone ||
        report?.phoneNumber ||
        report?.mobile ||
        ''
      ).toString().toLowerCase();

      return uid.includes(query) ||
        firstname.includes(query) ||
        email.includes(query) ||
        phone.includes(query);
    });
  }, [reports, searchQuery]);

  const filteredReports = useMemo(() => searchFilteredReports, [searchFilteredReports]);

  const renderObjectCell = (value: any) => {
    if (value == null) return '-';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      const arr = value as any[];
      if (arr.length === 0) return '-';
      if (arr.every(v => typeof v !== 'object')) return arr.map(String).join(', ');
      const preview = arr.slice(0, 3).map((item, i) => typeof item === 'object' ? JSON.stringify(item) : String(item));
      return preview.join(' | ');
    }
    // object
    const entries = Object.entries(value as Record<string, any>).slice(0, 6);
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
        {entries.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' | ')}
      </Typography>
    );
  };

  const columns: GridColDef[] = useMemo(() => {
    if (!filteredReports || filteredReports.length === 0) return [];
    const sampleKeys = new Set<string>();
    const dataKeySet = new Set<string>();
    filteredReports.slice(0, 3).forEach((row) => {
      Object.keys(row || {}).forEach((k) => sampleKeys.add(k));
      const dataObj = (row && (row.data ?? row.Data));
      if (dataObj && typeof dataObj === 'object' && !Array.isArray(dataObj)) {
        Object.keys(dataObj).forEach((dk) => dataKeySet.add(dk));
      }
    });

    // Exclude unwanted top-level keys and let 'data' expand into sub-columns
    const keys = Array.from(sampleKeys).filter(k => k !== 'id' && k !== 'profile' && k !== 'profileData' && k !== 'ProfileData' && k !== 'data' && k !== 'Data');

    const topLevelCols: GridColDef[] = keys.map((key) => ({
      field: key,
      headerName: key,
      flex: 1.2,
      sortable: true,
      renderCell: (params) => renderObjectCell(params.row[key]),
      valueGetter: (params) => {
        const val = params.row[key];
        if (val == null) return '-';
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val as any;
        if (Array.isArray(val)) return (val as any[]).every(v => typeof v !== 'object') ? (val as any[]).join(', ') : JSON.stringify(val);
        return JSON.stringify(val);
      },
    }));

    const preferredDataKeys = [

      'diastolic',
      'pulse',
      'rbs',
      'spo2',
      'systolic',
      'Wellness',
      'bca_link',
      'bca_report',
      'prakriti',
      'Report'

    ];

    const presentDataKeys = preferredDataKeys.filter(k => dataKeySet.has(k));

    const dataCols: GridColDef[] = presentDataKeys.map((dk) => {
      const base: GridColDef = {
        field: `data.${dk}`,
        headerName: dk,
        flex: 1.2,
        sortable: true,
        valueGetter: (params) => {
          const val = (params.row?.data?.[dk] ?? params.row?.Data?.[dk]);
          if (val == null) return '-';
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val as any;
          if (Array.isArray(val)) return (val as any[]).every(v => typeof v !== 'object') ? (val as any[]).join(', ') : JSON.stringify(val);
          return JSON.stringify(val);
        },
      };
      if (dk === 'bca_report') {
        return {
          ...base,
          renderCell: (params) => (
            <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openBcaDialog(params.row?.data?.[dk] ?? params.row?.Data?.[dk]); }}>
              View
            </Button>
          ),
        } as GridColDef;
      } else if (dk === 'prakriti') {
        return {
          ...base,
          renderCell: (params) => (
            <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openPrakritiDialog(params.row?.data?.[dk] ?? params.row?.Data?.[dk]); }}>
              View
            </Button>
          ),
        } as GridColDef;
      } else if (dk === 'PrakritiCollection') {
        return {
          ...base,
          headerName: 'Prakriti',
          renderCell: (params) => (
            <Button size="small" variant="contained" onClick={(e) => {
              e.stopPropagation();
              const full = params.row?.data?.prakriti ?? params.row?.Data?.prakriti ?? params.row?.data ?? params.row?.Data ?? params.row;
              openPrakritiDialog(full);
            }}>
              View
            </Button>
          ),
        } as GridColDef;
      } else if (dk === 'Wellness') {
        return {
          ...base,
          renderCell: (params) => {
            const value = params.row?.data?.[dk] ?? params.row?.Data?.[dk];
            return renderWellnessIcons(value, (e) => {
              e.stopPropagation();
              openWellnessDialog(value);
            });
          },
          valueGetter: (params) => {
            const value = params.row?.data?.[dk] ?? params.row?.Data?.[dk];
            return getSelectedWellnessNames(value);
          },
        } as GridColDef;
      } else if (dk === 'Report') {
        return {
          ...base,
          headerName: 'Report',
          renderCell: (params) => (
            <Button size="small" variant="contained" onClick={(e) => {
              e.stopPropagation();
              openReportDialog(params.row?.data?.[dk] ?? params.row?.Data?.[dk] ?? params.row);
            }}>
              View
            </Button>
          ),
        } as GridColDef;
      }
      return {
        ...base,
        renderCell: (params) => renderObjectCell(params.row?.data?.[dk] ?? params.row?.Data?.[dk]),
      } as GridColDef;
    });

    const desiredProfileFields: Array<{ key: string; header: string; width?: number }> = [
      { key: 'UID', header: 'UID', width: 200 },
      { key: 'firstname', header: 'First Name' },
      { key: 'gender', header: 'Gender' },
      { key: 'dob', header: 'DOB' },
      { key: 'height', header: 'Height' },
    ];

    const hasProfileData = filteredReports.some(r => !!(r?.profileData ?? r?.ProfileData));

    const profileCols: GridColDef[] = hasProfileData
      ? desiredProfileFields
        .filter(df => filteredReports.some(r => {
          const pd = (r?.profileData ?? r?.ProfileData) as any;
          return pd && (pd[df.key] !== undefined && pd[df.key] !== null && pd[df.key] !== '');
        }))
        .map(df => {
          const col: GridColDef = {
            field: `profileData.${df.key}`,
            headerName: df.header,
            width: df.width,
            flex: df.width ? undefined : 1.1,
            sortable: true,
            valueGetter: (params) => {
              const pd = (params.row?.profileData ?? params.row?.ProfileData) as any;
              return (pd && (pd[df.key] ?? pd[df.key as keyof typeof pd])) ?? '-';
            },
          };
          // Light formatting for specific fields
          if (df.key === 'dob') {
            col.valueFormatter = (params) => {
              const v: any = params.value;
              if (!v) return '-';
              let d: Date | null = null;
              if (typeof v === 'number') d = new Date(v);
              else if (typeof v === 'string') {
                // Check if it's ddMMyyyy format (8 digits)
                if (/^\d{8}$/.test(v)) {
                  const day = v.substring(0, 2);
                  const month = v.substring(2, 4);
                  const year = v.substring(4, 8);

                  // Handle edge case like 00002002 - just show year
                  if (day === '00' && month === '00') {
                    return year;
                  }

                  d = new Date(`${year}-${month}-${day}`);
                } else {
                  const num = /^\d+$/.test(v) ? Number(v) : NaN;
                  d = isNaN(num) ? new Date(v) : new Date(num);
                }
              } else if (v instanceof Date) d = v;
              if (!d || isNaN(d.getTime())) return String(v);
              try { return format(d, 'dd MMM yyyy'); } catch { return d.toISOString().slice(0, 10); }
            };
          } else if (df.key === 'height') {
            col.valueFormatter = (params) => {
              const v: any = params.value;
              if (v == null || v === '') return '-';
              const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
              if (!isNaN(n)) return `${n} cm`;
              return String(v);
            };
          } else if (df.key === 'gender') {
            col.valueFormatter = (params) => {
              const v: any = params.value;
              if (!v) return '-';
              const s = String(v).toLowerCase();
              return s.charAt(0).toUpperCase() + s.slice(1);
            };
          } else if (df.key === 'firstname') {
            col.valueFormatter = (params) => {
              const v: any = params.value;
              if (!v) return '-';
              const s = String(v);
              return s.charAt(0).toUpperCase() + s.slice(1);
            };
          }
          return col;
        })
      : [];

    const healthCols: GridColDef[] = [
      {
        field: 'healthIndex',
        headerName: 'Health Index',
        width: 130,
        sortable: false,
        valueGetter: (params) => {
          const d = params.row?.data ?? params.row?.Data ?? {};
          const profile = params.row?.profileData ?? params.row?.ProfileData;
          const vikriti = d?.vikriti ?? d?.Vikriti;
          const systolic = d?.systolic;
          const diastolic = d?.diastolic;
          const spo2 = d?.spo2;
          const pulse = d?.pulse;
          const rbs = d?.rbs;
          const handgripStrength = d?.handgrip ?? d?.handgripStrength ?? d?.handGripStrength;
          const result = calculateKioskHealthScore({ vikriti, systolic, diastolic, spo2, pulse, rbs, profile, handgripStrength });
          return result?.healthIndex ?? null;
        },
      },
      {
        field: 'healthStatus',
        headerName: 'Health Status',
        width: 140,
        sortable: false,
        valueGetter: (params) => {
          const d = params.row?.data ?? params.row?.Data ?? {};
          const profile = params.row?.profileData ?? params.row?.ProfileData;
          const vikriti = d?.vikriti ?? d?.Vikriti;
          const systolic = d?.systolic;
          const diastolic = d?.diastolic;
          const spo2 = d?.spo2;
          const pulse = d?.pulse;
          const rbs = d?.rbs;
          const handgripStrength = d?.handgrip ?? d?.handgripStrength ?? d?.handGripStrength;
          const result = calculateKioskHealthScore({ vikriti, systolic, diastolic, spo2, pulse, rbs, profile, handgripStrength });
          return result?.status ?? '';
        },
      },
    ];

    return [
      ...profileCols,
      ...topLevelCols,
      ...healthCols,
      ...dataCols,
    ];
  }, [filteredReports]);

  const bcaAnalytics = useMemo(() => {

    const rows = filteredReports || [];

    const asObj = (val: any) => {
      if (!val) return null;
      if (typeof val === 'string') { try { return JSON.parse(val); } catch { return null; } }
      if (typeof val === 'object') return val;
      return null;
    };
    const toNum = (v: any): number | null => {
      if (v == null || v === '') return null;
      if (typeof v === 'number') return isFinite(v) ? v : null;
      const n = Number(String(v).replace(/[^\d.\-]/g, ''));
      return isNaN(n) ? null : n;
    };
    const findNumeric = (obj: any, keys: string[]): number | null => {
      if (!obj) return null;

      for (const k of Object.keys(obj)) {
        const lower = k.toLowerCase();
        const match = keys.find(t => lower === t);
        if (match) {
          const n = toNum(obj[k]);
          if (n != null) return n;
        }
      }

      for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (v && typeof v === 'object') {
          const n = findNumeric(v, keys);
          if (n != null) return n;
        }
      }
      return null;
    };

    let bmiSum = 0, bmiCnt = 0;
    let fatSum = 0, fatCnt = 0;
    let muscleSum = 0, muscleCnt = 0;
    let visceralSum = 0, visceralCnt = 0;
    let waterSum = 0, waterCnt = 0;
    let rowsWithBca = 0;

    const bmiBuckets = [
      { key: 'Underweight (<18.5)', test: (v?: number | null) => v != null && v < 18.5, count: 0 },
      { key: 'Normal (18.5-24.9)', test: (v?: number | null) => v != null && v >= 18.5 && v <= 24.9, count: 0 },
      { key: 'Overweight (25-29.9)', test: (v?: number | null) => v != null && v >= 25 && v <= 29.9, count: 0 },
      { key: 'Obese (>=30)', test: (v?: number | null) => v != null && v >= 30, count: 0 },
    ];

    const bodyFatBuckets = [
      { key: '<15%', test: (v?: number | null) => v != null && v < 15, count: 0 },
      { key: '15-20%', test: (v?: number | null) => v != null && v >= 15 && v <= 20, count: 0 },
      { key: '21-25%', test: (v?: number | null) => v != null && v >= 21 && v <= 25, count: 0 },
      { key: '26-30%', test: (v?: number | null) => v != null && v >= 26 && v <= 30, count: 0 },
      { key: '>30%', test: (v?: number | null) => v != null && v > 30, count: 0 },
    ];

    const visceralBuckets = [
      { key: '<10', test: (v?: number | null) => v != null && v < 10, count: 0 },
      { key: '10-14', test: (v?: number | null) => v != null && v >= 10 && v <= 14, count: 0 },
      { key: '15-20', test: (v?: number | null) => v != null && v >= 15 && v <= 20, count: 0 },
      { key: '>20', test: (v?: number | null) => v != null && v > 20, count: 0 },
    ];

    const waterBuckets = [
      { key: '<50%', test: (v?: number | null) => v != null && v < 50, count: 0 },
      { key: '50-55%', test: (v?: number | null) => v != null && v >= 50 && v <= 55, count: 0 },
      { key: '56-60%', test: (v?: number | null) => v != null && v >= 56 && v <= 60, count: 0 },
      { key: '>60%', test: (v?: number | null) => v != null && v > 60, count: 0 },
    ];

    const lowercaseKeys = (obj: any): Record<string, any> => {
      if (!obj || typeof obj !== 'object') return {};
      return Object.fromEntries(Object.keys(obj).map(k => [k.toLowerCase(), obj[k]]));
    };

    const findAnyBcaObject = (obj: any): any | null => {
      if (!obj || typeof obj !== 'object') return null;
      const lobj = lowercaseKeys(obj);
      const typical = ['bmi', 'bodyfat', 'body_fat', 'muscle', 'visceralfat', 'water', 'bhbmi', 'bhbodyfatrate', 'bhmusclekg', 'bhvfal', 'bhwaterrate', 'bodyinfo'];
      const hasTypical = Object.keys(lobj).some(k => typical.includes(k));
      if (hasTypical) return obj;
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'object' && v) {
          const keyL = k.toLowerCase();
          if (keyL.includes('bca')) return v;
          const nested = findAnyBcaObject(v);
          if (nested) return nested;
        }
      }
      return null;
    };

    const unwrapBodyInfo = (root: any): any | null => {
      if (!root || typeof root !== 'object') return null;
      let cur: any = root;
      let safety = 0;

      while (cur && safety < 8 && (cur.next || cur.value)) {
        if (cur.next) { cur = cur.next; safety++; continue; }
        break;
      }
      const candidate = cur?.value?.bodyInfo ?? root?.value?.bodyInfo ?? root?.bodyInfo ?? null;
      return candidate || null;
    };

    rows.forEach((r) => {
      const d = r?.data ?? r?.Data ?? {};
      const rawCandidates = [
        d?.bca_report, d?.bcaReport, d?.bca, d?.BCA, d?.Bca,
        d?.Report?.bca_report, d?.Report?.BCA, r?.bca_report, r?.BCA,
      ];
      let rep: any = null;
      for (const cand of rawCandidates) { rep = asObj(cand); if (rep) break; }
      if (!rep) rep = findAnyBcaObject(d);
      if (!rep) return;

      const bodyInfo = unwrapBodyInfo(rep);
      const source = bodyInfo || rep;

      const bmi = findNumeric(source, ['bmi', 'bhbmi']);
      const bodyFat = findNumeric(source, ['bodyfat', 'body_fat', 'fat', 'body fat', 'bodyfatpercent', 'body_fat_%', 'fatpercentage', 'bhbodyfatrate']);
      const muscle = findNumeric(source, ['muscle', 'musclemass', 'skeletalmuscle', 'skeletal_muscle', 'muscle_mass', 'bhmusclekg', 'bhskeletalmusclekg']);
      const visceral = findNumeric(source, ['visceralfat', 'visceral_fat', 'vf', 'visceral', 'bhvfal']);
      const water = findNumeric(source, ['water', 'bodywater', 'body_water', 'waterpercentage', 'bhwaterrate']);

      if (bmi != null || bodyFat != null || muscle != null || visceral != null || water != null) rowsWithBca++;
      if (bmi != null) { bmiSum += bmi; bmiCnt++; const b = bmiBuckets.find(b => b.test(bmi)); if (b) b.count++; }
      if (bodyFat != null) { fatSum += bodyFat; fatCnt++; const b = bodyFatBuckets.find(b => b.test(bodyFat)); if (b) b.count++; }
      if (muscle != null) { muscleSum += muscle; muscleCnt++; }
      if (visceral != null) { visceralSum += visceral; visceralCnt++; const b = visceralBuckets.find(b => b.test(visceral)); if (b) b.count++; }
      if (water != null) { waterSum += water; waterCnt++; const b = waterBuckets.find(b => b.test(water)); if (b) b.count++; }
    });

    const avg = (s: number, c: number) => (c ? s / c : null);
    const toPairs = (arr: { key: string; count: number }[]) => arr.map(i => [i.key, i.count] as [string, number]);

    return {
      avgBmi: avg(bmiSum, bmiCnt),
      avgBodyFat: avg(fatSum, fatCnt),
      avgMuscle: avg(muscleSum, muscleCnt),
      avgVisceral: avg(visceralSum, visceralCnt),
      avgWater: avg(waterSum, waterCnt),
      bmiDist: toPairs(bmiBuckets),
      bodyFatDist: toPairs(bodyFatBuckets),
      visceralDist: toPairs(visceralBuckets),
      waterDist: toPairs(waterBuckets),
      count: rows.length,
      rowsWithBca,
    };
  }, [filteredReports]);

  useEffect(() => {
    if (!columns || columns.length === 0) return;
    setVisibleFields((prev) => (prev && prev.length ? prev.filter(f => columns.some(c => c.field === f)) : columns.map(c => c.field)));
  }, [columns]);

  const displayColumns: GridColDef[] = useMemo(() => {
    if (!visibleFields) return columns;
    const set = new Set(visibleFields);
    return columns.filter(c => set.has(c.field));
  }, [columns, visibleFields]);

  const analytics = useMemo(() => {
    const rows = filteredReports || [];
    const total = rows.length;
    const uidSet = new Set<string | number>();
    let sysSum = 0, sysCnt = 0;
    let diaSum = 0, diaCnt = 0;
    let pulseSum = 0, pulseCnt = 0;
    let rbsSum = 0, rbsCnt = 0;
    let spo2Sum = 0, spo2Cnt = 0;
    let healthIndexSum = 0, healthIndexCnt = 0;
    const healthStatusCounts: Record<string, number> = {};
    const wellnessCounts: Record<string, number> = {};

    // Local helpers for age/gender based health index aggregation
    const ageFromDob = (dob: any): number | null => {
      if (!dob) return null;
      let d: Date | null = null;
      if (typeof dob === 'number') d = new Date(dob);
      else if (typeof dob === 'string') {
        if (/^\d{8}$/.test(dob)) {
          const day = dob.substring(0, 2);
          const month = dob.substring(2, 4);
          const year = dob.substring(4, 8);
          if (day === '00' && month === '00') return null;
          d = new Date(`${year}-${month}-${day}`);
        } else {
          const num = /^\d+$/.test(dob) ? Number(dob) : NaN;
          d = isNaN(num) ? new Date(dob) : new Date(num);
        }
      } else if (dob instanceof Date) d = dob;
      if (!d || isNaN(d.getTime())) return null;
      const diff = Date.now() - d.getTime();
      const age = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
      return age >= 0 && age < 130 ? age : null;
    };

    const ageBuckets = [
      { key: '<18', test: (a: number) => a < 18 },
      { key: '18-24', test: (a: number) => a >= 18 && a <= 24 },
      { key: '25-34', test: (a: number) => a >= 25 && a <= 34 },
      { key: '35-44', test: (a: number) => a >= 35 && a <= 44 },
      { key: '45-54', test: (a: number) => a >= 45 && a <= 54 },
      { key: '55-64', test: (a: number) => a >= 55 && a <= 64 },
      { key: '65+', test: (a: number) => a >= 65 },
    ];

    const healthByAge: Record<string, { sum: number; count: number }> = {};
    const healthByGender: Record<string, { sum: number; count: number }> = {};

    const bump = (map: Record<string, { sum: number; count: number }>, key: string, val: number) => {
      if (!key) return;
      if (!map[key]) map[key] = { sum: 0, count: 0 };
      map[key].sum += val;
      map[key].count += 1;
    };

    const numVal = (v: any): number | null => {
      if (v == null || v === '') return null;
      if (typeof v === 'number') return isFinite(v) ? v : null;
      const n = Number(String(v).replace(/[^\d.\-]/g, ''));
      return isNaN(n) ? null : n;
    };

    const parseWellness = (val: any) => {
      let w = val;
      if (typeof w === 'string') { try { w = JSON.parse(w); } catch { /* ignore */ } }
      return w && typeof w === 'object' ? w : null;
    };

    rows.forEach((r) => {
      const uid = (r?.profileData?.UID ?? r?.ProfileData?.UID ?? r?.uid ?? r?.UID);
      if (uid != null) uidSet.add(uid as any);

      const d = r?.data ?? r?.Data ?? {};
      const s = numVal(d?.systolic); if (s != null) { sysSum += s; sysCnt++; }
      const di = numVal(d?.diastolic); if (di != null) { diaSum += di; diaCnt++; }
      const pu = numVal(d?.pulse); if (pu != null) { pulseSum += pu; pulseCnt++; }
      const rb = numVal(d?.rbs); if (rb != null) { rbsSum += rb; rbsCnt++; }
      const sp = numVal(d?.spo2); if (sp != null) { spo2Sum += sp; spo2Cnt++; }

      // Health index & status per row: use precomputed values from fetchReports
      const idx = r && typeof (r as any).healthIndex === 'number' ? Number((r as any).healthIndex) : NaN;
      if (Number.isFinite(idx)) {
        healthIndexSum += idx;
        healthIndexCnt += 1;

        // Age bucket aggregation
        const pd: any = r?.profileData ?? r?.ProfileData ?? {};
        const ageVal = ageFromDob(pd?.dob ?? pd?.DOB ?? pd?.dateOfBirth);
        if (ageVal != null) {
          const bucket = ageBuckets.find(b => b.test(ageVal));
          if (bucket) bump(healthByAge, bucket.key, idx);
        }

        // Gender aggregation
        const gRaw = pd?.gender ?? pd?.Gender;
        if (gRaw) {
          const gStr = String(gRaw).trim().toLowerCase();
          const norm = gStr.startsWith('m') ? 'Male'
            : gStr.startsWith('f') ? 'Female'
            : 'Other';
          bump(healthByGender, norm, idx);
        }
      }

      const status = r && typeof (r as any).healthStatus === 'string' ? (r as any).healthStatus : '';
      if (status) {
        healthStatusCounts[status] = (healthStatusCounts[status] || 0) + 1;
      }

      const w = parseWellness(d?.Wellness);
      const cats: any[] = Array.isArray(w?.Categories) ? w.Categories : [];
      cats.filter(c => c?.selected).forEach((c) => {
        const name = c?.name || 'Unknown';
        wellnessCounts[name] = (wellnessCounts[name] || 0) + 1;
      });
    });

    const avg = (sum: number, cnt: number) => (cnt ? (sum / cnt) : null);
    const wellnessEntries = Object.entries(wellnessCounts).sort((a, b) => b[1] - a[1]);
    const topWellness = wellnessEntries.slice(0, 5);
    const wellnessTotal = wellnessEntries.reduce((s, [, c]) => s + c, 0);

    const healthIndexByAge = Object.entries(healthByAge).map(([bucket, v]) => ({ bucket, avg: v.count ? v.sum / v.count : 0 }));
    const healthIndexByGender = Object.entries(healthByGender).map(([gender, v]) => ({ gender, avg: v.count ? v.sum / v.count : 0 }));

    return {
      total,
      uniqueUsers: uidSet.size,
      avgSystolic: avg(sysSum, sysCnt),
      avgDiastolic: avg(diaSum, diaCnt),
      avgPulse: avg(pulseSum, pulseCnt),
      avgRbs: avg(rbsSum, rbsCnt),
      avgSpo2: avg(spo2Sum, spo2Cnt),
      avgHealthIndex: avg(healthIndexSum, healthIndexCnt),
      healthStatusDist: Object.entries(healthStatusCounts),
      healthIndexByAge,
      healthIndexByGender,
      topWellness,
      wellnessAll: wellnessEntries,
      wellnessTotal,
    };
  }, [filteredReports]);

  const PieChart: React.FC<{ data: Array<{ label: string; value: number; color: string }>; size?: number; thickness?: number }>
    = ({ data, size = 180, thickness = 18 }) => {
      const total = data.reduce((s, d) => s + (d.value || 0), 0);
      const radius = (size - thickness) / 2;
      const circumference = 2 * Math.PI * radius;
      let cumulative = 0;
      return (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
              style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#eee" strokeWidth={thickness} />
              {total > 0 && data.map((seg, idx) => {
                const portion = (seg.value || 0) / total;
                const dash = circumference * portion;
                const gap = circumference - dash;
                const offset = circumference * cumulative;
                cumulative += portion;
                return (
                  <circle key={idx}
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={thickness}
                    strokeDasharray={`${dash} ${gap}`}
                    strokeDashoffset={-offset}
                  />
                );
              })}
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Typography variant="h6" fontWeight={700}>{total}</Typography>
              <Typography variant="caption" color="text.secondary">Selections</Typography>
            </Box>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            {data.length ? data.map((seg, idx) => {
              const pct = total ? Math.round((seg.value / total) * 100) : 0;
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: seg.color, borderRadius: 0.5 }} />
                  <Typography variant="body2">{seg.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{seg.value} ({pct}%)</Typography>
                </Box>
              );
            }) : <Typography variant="body2">No data</Typography>}
          </Box>
        </Box>
      );
    };

  const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar variant="rounded" sx={{ bgcolor: 'action.hover', color: 'text.primary' }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" fontWeight={700}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const cohorts = useMemo(() => {
    const rows = filteredReports || [];

    const toNumber = (v: any): number | null => {
      if (v == null || v === '') return null;
      if (typeof v === 'number') return isFinite(v) ? v : null;
      const n = Number(String(v).replace(/[^\d.\-]/g, ''));
      return isNaN(n) ? null : n;
    };

    const ageFromDob = (dob: any): number | null => {
      if (!dob) return null;
      let d: Date | null = null;
      if (typeof dob === 'number') d = new Date(dob);
      else if (typeof dob === 'string') {
        // Check if it's ddMMyyyy format (8 digits)
        if (/^\d{8}$/.test(dob)) {
          const day = dob.substring(0, 2);
          const month = dob.substring(2, 4);
          const year = dob.substring(4, 8);

          // Handle edge case like 00002002 - skip age calculation for invalid dates
          if (day === '00' && month === '00') {
            return null;
          }

          d = new Date(`${year}-${month}-${day}`);
        } else {
          const num = /^\d+$/.test(dob) ? Number(dob) : NaN;
          d = isNaN(num) ? new Date(dob) : new Date(num);
        }
      } else if (dob instanceof Date) d = dob;
      if (!d || isNaN(d.getTime())) return null;
      const diff = Date.now() - d.getTime();
      const age = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
      return age >= 0 && age < 130 ? age : null;
    };

    // Buckets
    const ageBuckets = [
      { key: '<18', test: (a: number) => a < 18, count: 0 },
      { key: '18-24', test: (a: number) => a >= 18 && a <= 24, count: 0 },
      { key: '25-34', test: (a: number) => a >= 25 && a <= 34, count: 0 },
      { key: '35-44', test: (a: number) => a >= 35 && a <= 44, count: 0 },
      { key: '45-54', test: (a: number) => a >= 45 && a <= 54, count: 0 },
      { key: '55-64', test: (a: number) => a >= 55 && a <= 64, count: 0 },
      { key: '65+', test: (a: number) => a >= 65, count: 0 },
    ];

    const genderCounts: Record<string, number> = {};

    const bpBuckets = [
      { key: 'Normal', test: (s?: number | null, d?: number | null) => s != null && d != null && s < 120 && d < 80, count: 0 },
      { key: 'Elevated', test: (s?: number | null, d?: number | null) => s != null && d != null && s >= 120 && s <= 129 && d < 80, count: 0 },
      { key: 'HTN Stage 1', test: (s?: number | null, d?: number | null) => s != null && d != null && ((s >= 130 && s <= 139) || (d >= 80 && d <= 89)), count: 0 },
      { key: 'HTN Stage 2', test: (s?: number | null, d?: number | null) => s != null && d != null && (s >= 140 || d >= 90), count: 0 },
    ];

    const rbsBuckets = [
      { key: '< 90', test: (v?: number | null) => v != null && v < 90, count: 0 },
      { key: '90-110', test: (v?: number | null) => v != null && v >= 90 && v <= 110, count: 0 },
      { key: '111-140', test: (v?: number | null) => v != null && v >= 111 && v <= 140, count: 0 },
      { key: '> 140', test: (v?: number | null) => v != null && v > 140, count: 0 },
    ];

    const spo2Buckets = [
      { key: '< 90%', test: (v?: number | null) => v != null && v < 90, count: 0 },
      { key: '90-94%', test: (v?: number | null) => v != null && v >= 90 && v <= 94, count: 0 },
      { key: '95-100%', test: (v?: number | null) => v != null && v >= 95, count: 0 },
    ];

    const heightBuckets = [
      { key: '<150 cm', test: (v?: number | null) => v != null && v < 150, count: 0 },
      { key: '150-165', test: (v?: number | null) => v != null && v >= 150 && v <= 165, count: 0 },
      { key: '166-180', test: (v?: number | null) => v != null && v >= 166 && v <= 180, count: 0 },
      { key: '>180', test: (v?: number | null) => v != null && v > 180, count: 0 },
    ];

    rows.forEach((r) => {
      const pd: any = r?.profileData ?? r?.ProfileData ?? {};
      const age = ageFromDob(pd?.dob);
      if (age != null) {
        const b = ageBuckets.find(b => b.test(age));
        if (b) b.count++;
      }
      const g = String(pd?.gender ?? '').trim().toLowerCase();
      if (g) genderCounts[g.charAt(0).toUpperCase() + g.slice(1)] = (genderCounts[g.charAt(0).toUpperCase() + g.slice(1)] || 0) + 1;

      const d = r?.data ?? r?.Data ?? {};
      const s = toNumber(d?.systolic);
      const di = toNumber(d?.diastolic);
      const rb = toNumber(d?.rbs);
      const sp = toNumber(d?.spo2);
      const h = toNumber(pd?.height);

      const bp = bpBuckets.find(b => b.test(s, di)); if (bp) bp.count++;
      const rbb = rbsBuckets.find(b => b.test(rb)); if (rbb) rbb.count++;
      const spb = spo2Buckets.find(b => b.test(sp)); if (spb) spb.count++;
      const hb = heightBuckets.find(b => b.test(h)); if (hb) hb.count++;
    });

    const toPairs = (arr: { key: string; count: number }[]) => arr.map(i => [i.key, i.count] as [string, number]);
    const genderPairs = Object.entries(genderCounts);

    return {
      age: toPairs(ageBuckets),
      gender: genderPairs,
      bp: toPairs(bpBuckets),
      rbs: toPairs(rbsBuckets),
      spo2: toPairs(spo2Buckets),
      height: toPairs(heightBuckets),
      total: rows.length,
    };
  }, [filteredReports]);

  const calculateAverageSeverity = (severityMap: any[] = []): number => {
    if (!severityMap.length) return 0;

    const totalSeverityScore = severityMap.reduce(
      (sum, e) => sum + (e?.severity || 0),
      0
    );

    const meanSeverity = totalSeverityScore / severityMap.length;

    return parseFloat(meanSeverity.toFixed(2));
  };

  const calculateSymptomScore = (symptomCount: number): number => {
    if (symptomCount <= 0) return 0;
    if (symptomCount <= 2) return 1;
    if (symptomCount <= 4) return 2;
    if (symptomCount <= 6) return 3;
    if (symptomCount <= 8) return 4;
    return 5; // ≥9
  };

  const calculateBloodPressureScore = (systolic: number, diastolic: number): number => {
    if (systolic >= 180 || diastolic >= 110) return 5;
    if ((systolic >= 160 && systolic <= 179) || (diastolic >= 100 && diastolic <= 109)) return 3;
    if ((systolic >= 140 && systolic <= 159) || (diastolic >= 90 && diastolic <= 99)) return 2;
    if ((systolic >= 130 && systolic <= 139) || (diastolic >= 85 && diastolic <= 89)) return 1;
    if ((systolic >= 120 && systolic <= 129) || (diastolic >= 80 && diastolic <= 84)) return 0;
    return 0;
  };

  const calculateSpO2Score = (spo2: number): number => {
    if (spo2 >= 95) return 0;
    if (spo2 >= 90 && spo2 <= 94) return 2;
    if (spo2 >= 85 && spo2 <= 89) return 4;
    if (spo2 < 85) return 5;
    return 0;
  };

  const calculateRbsScore = (rbs: number): number => {
    if (rbs < 140) return 0;
    if (rbs >= 140 && rbs <= 199) return 3;
    if (rbs >= 200) return 5;
    return 0;
  };

  const calculateWaistScore = (gender: any, waistCm: number): number => {
    if (!gender || waistCm == null) return 0;

    const g = String(gender).toLowerCase();

    if (["male"].includes(g)) {
      if (waistCm < 90) return 0;
      if (waistCm >= 90 && waistCm <= 99) return 3;
      if (waistCm >= 100) return 5;
    }

    if (["female"].includes(g)) {
      if (waistCm < 80) return 0;
      if (waistCm >= 80 && waistCm <= 89) return 3;
      if (waistCm >= 90) return 5;
    }

    return 0;
  };

  const calculateHandgripScore = (handgripStrength: number): number => {
    if (!handgripStrength && handgripStrength !== 0) return 0;
    if (handgripStrength >= 70 && handgripStrength <= 90) return 0;
    if (handgripStrength >= 50 && handgripStrength < 70) return 1;
    if (handgripStrength >= 30 && handgripStrength < 50) return 2;
    if (handgripStrength >= 15 && handgripStrength < 30) return 3;
    if (handgripStrength >= 0 && handgripStrength < 15) return 5;
    return 0;
  };

  const calculateKioskHealthScore = (data: any, debug: boolean = false) => {
    const { vikriti, systolic, diastolic, spo2, pulse, rbs, profile, handgripStrength } = data;

    let vikritiObj: any = vikriti;
    if (typeof vikriti === 'string') {
      try {
        vikritiObj = JSON.parse(vikriti);
      } catch {
        vikritiObj = {};
      }
    }

    const vikritiList = vikritiObj?.vikritiList || vikritiObj?.VikritiList || [];
    const { waist, gender } = profile || {};

    const getNumeric = (value: any): any => {
      if (!value) return 0;
      const match = String(value).match(/[\d.]+/g);
      if (!match) return 0;
      return match.length > 1 ? match.map(Number) : parseFloat(match[0]);
    };

    let systolicVal = 0;
    let diastolicVal = 0;

    if (systolic && typeof systolic === 'string' && systolic.includes('/')) {
      const [sys, dia] = getNumeric(systolic);
      systolicVal = sys || 0;
      diastolicVal = dia || 0;
    } else {
   systolicVal = Array.isArray(getNumeric(systolic))
      ? getNumeric(systolic)[0]
      : getNumeric(systolic);
    diastolicVal = getNumeric(diastolic);
    }

    const spo2Val = getNumeric(spo2);
    const pulseVal = getNumeric(pulse);
    const rbsVal = getNumeric(rbs);
    const waistVal = getNumeric(waist);
    const handgripVal = getNumeric(handgripStrength);

    if (debug) {
      console.log('[HealthScore] Raw input', {
        systolic,
        diastolic,
        spo2,
        pulse,
        rbs,
        handgripStrength,
        waist,
        gender,
        vikritiObj,
      });

      console.log('[HealthScore] Parsed readings', {
        systolicVal,
        diastolicVal,
        spo2Val,
        pulseVal,
        rbsVal,
        waistVal,
        handgripVal,
        vikritiListLength: vikritiList?.length || 0,
      });
    }

    const symptomsSeverityScore = calculateAverageSeverity(vikritiList);
    const symptomsCountScore = calculateSymptomScore(vikritiList?.length || 0);
    const bloodPressureScore = calculateBloodPressureScore(systolicVal, diastolicVal);
    const spo2Score = calculateSpO2Score(spo2Val);
    const handgripScore = calculateHandgripScore(handgripVal);
    const rbsScore = calculateRbsScore(rbsVal);
    const waistScore = calculateWaistScore(gender, waistVal);

    const totalScore =
      symptomsSeverityScore + symptomsSeverityScore +
      symptomsCountScore + symptomsCountScore +
      bloodPressureScore +
      spo2Score +
      handgripScore +
      rbsScore +
      waistScore;

    const healthIndex = Math.max(0, 100 - (totalScore / 45) * 100);

    if (debug) {
      console.log('[HealthScore] Component scores', {
        symptomsSeverityScore,
        symptomsCountScore,
        bloodPressureScore,
        spo2Score,
        handgripScore,
        rbsScore,
        waistScore,
        totalScore,
        healthIndexRaw: healthIndex,
      });
    }

    let status = 'Good';
    if (healthIndex >= 90) status = 'Excellent';
    else if (healthIndex >= 75) status = 'Good';
    else if (healthIndex >= 60) status = 'Moderate';
    else if (healthIndex >= 40) status = 'Poor';
    else status = 'Very Poor / High Risk';

    const result = {
      readings: {
        systolicVal,
        diastolicVal,
        spo2Val,
        pulseVal,
        rbsVal,
        waistVal,
      },
      scores: {
        symptomsSeverityScore,
        symptomsCountScore,
        bloodPressureScore,
        spo2Score,
        handgripScore,
        rbsScore,
        waistScore,
      },
      totalScore,
      healthIndex: parseFloat(healthIndex.toFixed(1)),
      status,
    };

    if (debug) {
      console.log('[HealthScore] Final result', result);
    }

    return result;
  };

  const generateHealthIndexHTML = (healthIndex: number, scores: any) => {
    const safeIndex = Math.min(Math.max(healthIndex, 0), 100);
    let categoryLabel = '';
    if (safeIndex < 40) categoryLabel = 'Very Poor';
    else if (safeIndex < 60) categoryLabel = 'Poor';
    else if (safeIndex < 75) categoryLabel = 'Moderate';
    else if (safeIndex < 90) categoryLabel = 'Good';
    else categoryLabel = 'Excellent';

    return `
    <div  
      style="
        font-family:'Poppins',sans-serif;
        margin:20px;
        position:relative;
        background:#E8F3F3A8;
        padding:35px 20px 25px 20px;
        border-radius:20px;
      "
    >

      <div style="
        position:absolute;
        top:-14px;
        left:50%;
        transform:translateX(-50%);
        font-weight:600;
        background:#1C8686;
        background:linear-gradient(135deg, #16a085 0%, #1C8686 50%, #1e6f6f 100%);
        font-size:16px;
        text-align:center;
        border-radius:20px;
        padding:5px 18px;
        color:#FFFFFF;
        z-index:10;
        white-space:nowrap;
      ">
        Overall Health Index
      </div>

      <div class="range-wrapper" 
        style="position:relative;width:100%;max-width:620px;margin:auto;margin-top:10px;"
      >

        <div class="range-bar" style="
          position:relative;
          height:20px;
          border-radius:10px;
          background: linear-gradient(to right,
            #c0392b 0%,    #c0392b 39.9%, 
            #e67e22 40%,   #e67e22 59.9%,
            #f1c40f 60%,   #f1c40f 74.9%,
            #2ecc71 75%,   #2ecc71 89.9%, 
            #1e8449 90%,   #1e8449 100%
          );
        ">

          <div class="range-thumb" style="
            position:absolute;
            top:50%;
            left:${safeIndex}%;
            transform:translate(-50%, -50%);
            width:24px;
            height:24px;
            border-radius:50%;
            background:#000;
            border:2px solid #fff;
            box-shadow:0 0 4px rgba(0,0,0,0.4);
          "></div>

          ${[40, 60, 75, 90].map(val => `
            <div style="
              position:absolute;
              top:0;
              left:${val}%;
              width:1px;
              height:100%;
              background:rgba(255,255,255,0.7);
            "></div>
          `).join('')}
        </div>

        <div class="range-scale" style="
          display:flex;
          justify-content:space-between;
          font-size:10px;
          color:#666;
          margin-top:4px;
        ">
          ${Array.from({ length: 11 }, (_, i) => `<span>${i * 10}</span>`).join('')}
        </div>

        <div class="range-labels" style="
          display:flex;
          justify-content:space-between;
          font-size:11px;
          color:#333;
          margin-top:6px;
          font-weight:500;
        ">
          <span>&lt;40 Very Poor</span>
          <span>40–59 Poor</span>
          <span>60–74 Moderate</span>
          <span>75–89 Good</span>
          <span>90–100 Excellent</span>
        </div>
      </div>

      <div class="health-value" style="
        text-align:center;
        margin-top:25px;
        font-size:18px;
        font-weight:500;
        color:#1c8686;
      ">
        ${categoryLabel}: 
        <span style="font-weight:700; font-size:22px;">
          ${healthIndex.toFixed(1)}%
        </span>
      </div>

    </div>
  `;
  };

  const getByPath = (obj: any, path: string): any => {
    if (!obj || !path) {
      console.log('getByPath: Invalid input -', { obj: !!obj, path });
      return undefined;
    }
    const parts = path.split('.');
    let cur: any = obj;
    for (const p of parts) {
      if (cur == null) {
        console.log(`getByPath: Path '${path}' - '${p}' is null/undefined`);
        return undefined;
      }
      if (!(p in cur)) {
        console.log(`getByPath: Path '${path}' - property '${p}' not found in`, Object.keys(cur));
        return undefined;
      }
      cur = cur[p];
    }
    console.log(`getByPath: Successfully got value for path '${path}':`, cur);
    return cur;
  };

  const toCsvCell = (v: any): string => {
    let s: string;
    if (v === undefined || v === null || v === '-') s = '';
    else if (typeof v === 'object') s = Array.isArray(v) ? v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(' | ') : JSON.stringify(v);
    else s = String(v);
    // Escape CSV
    const needsQuote = /[",\n]/.test(s);
    const esc = s.replace(/"/g, '""');
    return needsQuote ? `"${esc}"` : esc;
  };

  interface BcaData {
    // Core measurements
    Weight?: number | string;
    Height?: number | string;
    BMI?: number | string;
    BodyFat?: number | string;
    VisceralFat?: number | string;
    MuscleMass?: number | string;
    BoneMass?: number | string;
    BMR?: number | string;
    BodyAge?: number | string;
    Water?: number | string;
    Protein?: number | string;

    // Additional fields that might be present
    [key: string]: any;
  }

  interface PrakritiData {
    vata?: number | string;
    pitta?: number | string;
    kapha?: number | string;
    result?: string;
    [key: string]: any;
  }

  const extractPrakritiData = (row: any): PrakritiData => {
    try {
      const dataObj = row?.Data || row?.data;
      if (!dataObj) return {};

      // Look for Prakriti data in various possible locations within Data
      const prakritiData = dataObj.prakriti || dataObj.Prakriti || dataObj.prakriti_data ||
        dataObj.PrakritiData || dataObj.prakriti_report;

      if (!prakritiData) return {};

      let parsed: any = prakritiData;

      // If it's a string, try to parse it as JSON
      if (typeof prakritiData === 'string') {
        try {
          parsed = JSON.parse(prakritiData);
        } catch (e) {
          console.warn('Failed to parse Prakriti data as JSON:', e);
          return {};
        }
      }

      // Extract the main fields we want
      const result: PrakritiData = {};

      // Look for vata, pitta, kapha in various case formats
      if (parsed?.vata !== undefined) result.vata = parsed.vata;
      else if (parsed?.Vata !== undefined) result.vata = parsed.Vata;

      if (parsed?.pitta !== undefined) result.pitta = parsed.pitta;
      else if (parsed?.Pitta !== undefined) result.pitta = parsed.Pitta;

      if (parsed?.kapha !== undefined) result.kapha = parsed.kapha;
      else if (parsed?.Kapha !== undefined) result.kapha = parsed.Kapha;

      if (parsed?.result !== undefined) result.result = parsed.result;
      else if (parsed?.Result !== undefined) result.result = parsed.Result;

      return result;
    } catch (e) {
      console.warn('Error extracting Prakriti data:', e);
      return {};
    }
  };

  const extractBcaData = (row: any): BcaData => {
    try {
      const dataObj = row?.Data || row?.data;
      if (!dataObj) return {};

      // Look for BCA data in various possible locations within Data
      const bcaData = dataObj.bca_report || dataObj.bcaData || dataObj.bca_report_data ||
        dataObj.bodyInfo || dataObj.body_info || dataObj.bodyInfoData ||
        dataObj.report_data;

      if (!bcaData) {
        console.log('No BCA data found in row');
        return {};
      }

      let parsed: any = bcaData;

      // If it's a string, try to parse it as JSON
      if (typeof bcaData === 'string') {
        try {
          parsed = JSON.parse(bcaData);
          // Handle double-encoded JSON
          if (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed);
            } catch (e) {
              console.log('Failed to parse double-encoded JSON, using as-is');
            }
          }
        } catch (e) {
          console.warn('Failed to parse BCA data as JSON:', e);
          return {};
        }
      }

      console.log('Parsed BCA data structure:', JSON.stringify(parsed, null, 2));

      if (parsed?.next?.value?.bodyInfo) {
        parsed = parsed.next.value.bodyInfo;
      }
      // Handle direct bodyInfo
      else if (parsed?.bodyInfo) {
        parsed = parsed.bodyInfo;
      }
      // Handle case where parsed is already the bodyInfo object
      else if (parsed?.bhBMI !== undefined || parsed?.bhWeightKg !== undefined) {
        // Already have the right structure
      }
      // Handle case where bca_report is a string that needs parsing
      else if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
          // Check if we need to go deeper
          if (parsed?.next?.value?.bodyInfo) {
            parsed = parsed.next.value.bodyInfo;
          } else if (parsed?.bodyInfo) {
            parsed = parsed.bodyInfo;
          }
        } catch (e) {
          console.warn('Failed to parse BCA data string:', e);
        }
      }

      const result: BcaData = {};

      const fieldMappings: Record<string, string> = {
        'weight': 'Weight',
        'Weight': 'Weight',
        'bhWeight': 'Weight',
        'bhWeightKg': 'Weight',
        'bhWeightKgCon': 'WeightChange',
        'weight_kg': 'Weight',
        'bhIdealWeightKg': 'IdealWeight',
        'bhWeightKgListStandard': 'StandardWeight',
        'display': 'WeightDisplay',

        'height': 'Height',
        'Height': 'Height',
        'bhHeight': 'Height',
        'height_cm': 'Height',

        'bmi': 'BMI',
        'BMI': 'BMI',
        'bhBMI': 'BMI',
        'bhBmi': 'BMI',
        'bmi_value': 'BMI',
        'bhBMIListStandard': 'StandardBMI',

        'bodyFat': 'BodyFat',
        'body_fat': 'BodyFat',
        'bhBodyFatRate': 'BodyFat',
        'body_fat_percentage': 'BodyFat',
        'bhBodyFatKg': 'BodyFatKg',
        'bhBodyFatKgCon': 'BodyFatChange',
        'bhBodyFatRateListStandard': 'StandardBodyFat',

        'visceralFat': 'VisceralFat',
        'visceral_fat': 'VisceralFat',
        'bhVfal': 'VisceralFat',
        'bhVFAL': 'VisceralFat',
        'visceral_fat_level': 'VisceralFat',
        'bhVFALListStandard': 'StandardVisceralFat',

        'muscleMass': 'MuscleMass',
        'muscle_mass': 'MuscleMass',
        'bhMuscleKg': 'MuscleMass',
        'bhMuscleKgCon': 'MuscleMassChange',
        'bhMuscleRate': 'MuscleRate',
        'bhSkeletalMuscleKg': 'SkeletalMuscle',
        'bhSkeletalMuscleRate': 'SkeletalMuscleRate',
        'muscle_mass_kg': 'MuscleMass',
        'bhMuscleRateListStandard': 'StandardMuscleRate',

        'boneMass': 'BoneMass',
        'bone_mass': 'BoneMass',
        'bhBoneKg': 'BoneMass',
        'bhBoneRate': 'BoneRate',
        'bone_mass_kg': 'BoneMass',

        'bmr': 'BMR',
        'BMR': 'BMR',
        'bhBMR': 'BMR',
        'bhBmr': 'BMR',
        'bhAMR': 'AMR',
        'basal_metabolic_rate': 'BMR',
        'bhBMRLevelListStandard': 'StandardBMR',

        'bodyAge': 'BodyAge',
        'body_age': 'BodyAge',
        'bhBodyAge': 'BodyAge',
        'metabolic_age': 'BodyAge',

        'water': 'Water',
        'Water': 'Water',
        'bhWaterRate': 'Water',
        'bhWaterKg': 'WaterKg',
        'water_percentage': 'Water',
        'bhWaterRateListStandard': 'StandardWater',

        'protein': 'Protein',
        'Protein': 'Protein',
        'bhProteinRate': 'Protein',
        'bhProteinKg': 'ProteinKg',
        'protein_percentage': 'Protein',
        'bhProteinRateListStandard': 'StandardProtein',

        'bhBodyScore': 'BodyScore',
        'bhBodyType': 'BodyType',
        'bhBodyFatFreeMassKg': 'FatFreeMass',
        'bhBodyFatSubCutKg': 'SubcutaneousFat',
        'bhBodyFatSubCutRate': 'SubcutaneousFatRate',
        'bhCI': 'CorpulenceIndex',
        'bhMuscleStorageLevel': 'MuscleStorageLevel',
        'heartRate': 'HeartRate',
        'resistance': 'Resistance',
        'algVersion': 'AlgorithmVersion'
      };

      Object.entries(parsed).forEach(([key, value]) => {
        if (value === null || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length > 0)) {
          return;
        }

        const standardField = fieldMappings[key] || key;

        if (standardField && !result[standardField as keyof BcaData]) {
          result[standardField as keyof BcaData] = value as any;
        }
      });

      return result;

    } catch (e) {
      console.error('Error parsing BCA data:', e);
      return {};
    }
  };

  const exportCsv = (selectedOnly: boolean = false) => {
    try {
      const rowsAll = filteredReports || [];
      const rows = selectedOnly
        ? rowsAll.filter(r => rowSelection.includes((r?.profileData?.UID ?? r?.ProfileData?.UID ?? r?.uid ?? r?.UID ?? r?.id) as any))
        : rowsAll;
      const cols = displayColumns || [];
      if (!rows.length || !cols.length) return;

      console.log('First row data:', JSON.stringify(rows[0], null, 2));

      const allBcaFields = new Set<string>();
      const allBcaData: BcaData[] = rows.map((row) => {

        const possibleBcaPaths = [
          'bca_report', 'bcaData', 'data.bca_report', 'data.bcaData',
          'data.bca_report_data', 'data.bodyInfo', 'bodyInfo', 'data.body_info',
          'data.bodyInfoData', 'data.report_data'
        ];

        possibleBcaPaths.forEach(path => {
          const value = getByPath(row, path);
          if (value) {
            console.log(`Found BCA data at ${path}:`, typeof value === 'string' ? value : JSON.stringify(value));
          }
        });

        const bcaData = extractBcaData(row);
        console.log('Extracted BCA data:', bcaData);
        Object.keys(bcaData).forEach(field => allBcaFields.add(field));
        return bcaData;
      });

      const allPrakritiFields = new Set<string>();
      const allPrakritiData: PrakritiData[] = rows.map((row) => {
        const prakritiData = extractPrakritiData(row);
        Object.keys(prakritiData).forEach(field => allPrakritiFields.add(field));
        return prakritiData;
      });

      console.log('All BCA fields found:', Array.from(allBcaFields));

      const standardBcaFields = [
        'Weight', 'Height', 'BMI', 'BodyFat', 'VisceralFat',
        'MuscleMass', 'BoneMass', 'BMR', 'BodyAge', 'Water', 'Protein'
      ];

      // Sort fields: standard fields first, then any additional fields alphabetically
      const sortedBcaFields = [
        ...standardBcaFields.filter(f => allBcaFields.has(f)),
        ...Array.from(allBcaFields).filter(f => !standardBcaFields.includes(f)).sort()
      ];

      // Define standard Prakriti fields in desired order
      const standardPrakritiFields = ['vata', 'pitta', 'kapha', 'result'];
      const sortedPrakritiFields = [
        ...standardPrakritiFields.filter(f => allPrakritiFields.has(f)),
        ...Array.from(allPrakritiFields).filter(f => !standardPrakritiFields.includes(f)).sort()
      ];

      // Prepare headers - exclude bcaData, prakriti and other related fields
      const excludedFields = [
        'bcaData', 'bodyInfo', 'body_info', 'bodyData',
        'bcaReport', 'bca_report_data', 'bodyInfoData', 'report_data'
      ];

      const mainHeaders = cols
        .filter(col => !excludedFields.includes(col.field) && col.field !== 'data.bca_report' && col.field !== 'data.prakriti')
        .map(c => c.headerName || c.field);

      // Add BCA fields with proper formatting
      const bcaHeaders = sortedBcaFields.map(field => {
        // Convert camelCase to Title Case with spaces
        const formatted = field
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
        return `BCA ${formatted}`;
      });

      // Add Prakriti fields with proper formatting
      const prakritiHeaders = sortedPrakritiFields.map(field => {
        const formatted = field.charAt(0).toUpperCase() + field.slice(1);
        return `Prakriti ${formatted}`;
      });

      const allHeaders = [...mainHeaders, ...bcaHeaders, ...prakritiHeaders];

      // Start building CSV lines
      const lines: string[] = [allHeaders.join(',')];

      // Process each row
      rows.forEach((row, rowIndex) => {
        const bcaData = allBcaData[rowIndex];
        const prakritiData = allPrakritiData[rowIndex];
        const cells: string[] = [];

        // Add main columns
        cols.forEach((col) => {
          // Skip BCA-related columns since we're breaking them out (but allow bca_report in grid)
          if (['bcaData', 'bodyInfo', 'body_info', 'bcaReport', 'bca_report_data', 'bodyInfoData', 'report_data'].includes(col.field)) {
            return;
          }

          // Skip bca_report and prakriti specifically for CSV export since we extract their data into separate columns
          if (col.field === 'data.bca_report' || col.field === 'data.prakriti') {
            return;
          }

          let val: any;
          try {
            if (col.valueGetter) {
              val = (col.valueGetter as any)({ row, field: col.field, id: (row as any).id, colDef: col });
            } else {
              val = getByPath(row, col.field);
            }
            if (col.valueFormatter) {
              val = (col.valueFormatter as any)({ value: val, field: col.field, id: (row as any).id, row, api: undefined });
            }
          } catch (e) {
            console.warn(`Error getting value for column ${col.field}:`, e);
            val = '';
          }

          // Handle special column types
          if (col.field && col.field.toLowerCase().includes('prakriti')) {
            try {
              const parsed = typeof val === 'string' ? (() => {
                try { return JSON.parse(val); }
                catch { return null; }
              })() : val;

              if (parsed && typeof parsed === 'object') {
                const cleaned: any = { ...parsed };
                delete cleaned.PrakritiCollection;
                delete cleaned.prakritiCollection;
                delete cleaned.prakriti_collection;
                val = cleaned;
              }
            } catch (e) {
              console.warn('Error processing prakriti data:', e);
            }
          }

          cells.push(toCsvCell(val));
        });

        // Add BCA data cells
        sortedBcaFields.forEach(field => {
          // First try exact match
          let value = bcaData[field];

          // If not found, try case-insensitive search
          if (value === undefined || value === null) {
            const lowerField = field.toLowerCase();
            const matchingKey = Object.keys(bcaData).find(k => k.toLowerCase() === lowerField);
            if (matchingKey) {
              value = bcaData[matchingKey];
            }
          }

          // If still not found, try to find in any case
          if ((value === undefined || value === null) && field) {
            const fieldLower = field.toLowerCase();
            for (const [k, v] of Object.entries(bcaData)) {
              if (k.toLowerCase() === fieldLower) {
                value = v;
                break;
              }
            }
          }

          // Format the value for CSV
          let formattedValue = '';
          if (value !== undefined && value !== null) {
            // Handle numbers and numeric strings
            if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value))) {
              formattedValue = String(value);
            }
            // Handle other values
            else {
              formattedValue = toCsvCell(value);
            }
          }

          cells.push(formattedValue);
        });

        // Add Prakriti fields
        sortedPrakritiFields.forEach((field) => {
          const value = prakritiData[field];
          const formattedValue = value !== undefined && value !== null ? toCsvCell(value) : '';
          cells.push(formattedValue);
        });

        lines.push(cells.join(','));
      });

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kiosk_reports_${selectedOnly ? 'selected_' : ''}${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed:', e);
    }
  };

  return (
    <Box sx={{ px: 0, pt: 1.5, bgcolor: 'background.paper' }}>
      <Card sx={{ mb: 2, borderRadius: 0, boxShadow: 'none' }}>
        <CardContent sx={{ px: 1, py: 0 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="reports analytics tabs">
            <Tab value="reports" label="Reports" />
            <Tab value="analytics" label="Analytics" />
          </Tabs>
        </CardContent>
      </Card>
      {activeTab === 'reports' && (
        <Card sx={{ mb: 2, borderRadius: 0, boxShadow: 'none' }}>
          <CardContent sx={{ px: 1, py: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* First row: Date pickers and search */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, alignItems: 'flex-start' }}>
                <Box sx={{ width: '180px' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(v) => setStartDate(v)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ width: '180px' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(v) => setEndDate(v)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ width: '300px' }}>
                  <TextField
                    label="Search (UID, Name, Email, Phone)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    placeholder="Enter UID, firstname, email, or phone..."
                  />
                </Box>
              </Box>

              {/* Second row: Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={fetchReports} disabled={loading}>
                  {loading ? 'Loading…' : 'Refresh Reports'}
                </Button>
                <Button variant="outlined" onClick={() => exportCsv(false)} disabled={loading || !filteredReports?.length}>
                  Export CSV
                </Button>
                <Button variant="outlined" onClick={() => exportCsv(true)} disabled={loading || rowSelection.length === 0}>
                  Export Selected
                </Button>
                <Button variant="text" onClick={() => setColumnsDialogOpen(true)} disabled={!displayColumns?.length}>
                  Columns
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {activeTab === 'reports' && (
        <Card sx={{
          borderRadius: 0,
          boxShadow: 'none',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#555',
            },
          },
        }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#F8F2E3' }}>
            <Typography variant="h6" sx={{ color: '#75623f', fontWeight: 600 }}>
              Total Reports: {filteredReports.length}
            </Typography>
            <Typography variant="body2" sx={{ color: '#3B362A', mt: 0.5 }}>
              {filteredReports.length === reports.length
                ? `Showing all ${reports.length} reports`
                : `Showing ${filteredReports.length} of ${reports.length} reports (filtered)`
              }
            </Typography>
          </Box>
          <Box
            ref={dataGridContainerRef}
            sx={{
              width: '100%',
              maxWidth: '100%',
              position: 'relative',
              px: 0,
              overflowX: 'hidden',
              overflowY: 'visible',
            }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}>
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              rows={filteredReports}
              columns={displayColumns.map(column => ({
                ...column,
                flex: undefined, // Remove flex completely
                width: 150, // Reduced fixed width for each column
                minWidth: 150, // Minimum width for each column
                resizable: false, // Prevent resizing
              }))}
              getRowId={(row) => (row?.profileData?.UID ?? row?.ProfileData?.UID ?? row?.uid ?? row?.UID ?? row?.id)}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              autoHeight
              rowHeight={52}
              disableRowSelectionOnClick
              disableColumnMenu
              loading={loading}
              checkboxSelection
              rowSelectionModel={rowSelection}
              onRowSelectionModelChange={(m) => setRowSelection(m)}
              onRowClick={(p) => { setSelected(p.row); setDetailOpen(true); }}
              columnBuffer={10}
              hideFooterSelectedRowCount
              sx={{
                width: '100%',
                minWidth: '2000px', // Force minimum width to ensure overflow
                '& .MuiDataGrid-main': {
                  overflowX: 'auto',
                  width: '100%',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  minWidth: 'fit-content',
                },
                '& .MuiDataGrid-columnHeader': {
                  padding: '4px 8px',
                  '&:focus': {
                    outline: 'none',
                  },
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
                '& .MuiDataGrid-cell': {
                  padding: '4px 8px',
                  '&:focus': {
                    outline: 'none',
                  },
                },
                '& .MuiDataGrid-row': {
                  minWidth: 'fit-content',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    },
                  },
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto !important',
                  minWidth: 'fit-content',
                  scrollbarWidth: 'none', // Hide Firefox scrollbar
                  msOverflowStyle: 'none', // Hide IE scrollbar
                  '&::-webkit-scrollbar': {
                    display: 'none', // Hide Chrome/Safari scrollbar
                  },
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  minWidth: 'fit-content',
                },
                '& .MuiDataGrid-footerContainer': {
                  justifyContent: 'flex-start',
                  minWidth: 'fit-content',
                },
                '& .MuiTablePagination-root': {
                  marginLeft: 0,
                },
              }}
            />

            {/* Custom Horizontal Scroll Indicator */}
            <Box
              ref={scrollIndicatorRef}
              sx={{
                width: '100%',
                height: '20px',
                overflowX: 'auto',
                overflowY: 'hidden',
                mt: 1,
                mb: 1,
                scrollbarWidth: 'auto',
                scrollbarColor: '#1976d2 #e0e0e0',
                '&::-webkit-scrollbar': {
                  height: '16px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#e0e0e0',
                  borderRadius: '8px',
                  border: '2px solid #ccc',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#1976d2',
                  borderRadius: '8px',
                  border: '3px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&:active': {
                    backgroundColor: '#0d47a1',
                  },
                },
              }}
            >
              {/* Inner div to create scrollable width matching DataGrid */}
              <Box sx={{ width: '2000px', height: '1px' }} />
            </Box>
          </Box>
        </Card>
      )}

      {activeTab === 'analytics' && (
        <Card sx={{ borderRadius: 0, boxShadow: 'none' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Total Reports</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.total}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Unique Users</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.uniqueUsers}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg Systolic</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.avgSystolic ? analytics.avgSystolic.toFixed(1) : '-'}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg Diastolic</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.avgDiastolic ? analytics.avgDiastolic.toFixed(1) : '-'}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg Pulse</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.avgPulse ? analytics.avgPulse.toFixed(1) : '-'}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg RBS</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.avgRbs ? analytics.avgRbs.toFixed(1) : '-'}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg SpO₂</Typography>
                  <Typography variant="h5" fontWeight={700}>{analytics.avgSpo2 ? analytics.avgSpo2.toFixed(1) : '-'}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Avg Health Index</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {analytics.avgHealthIndex !== null && analytics.avgHealthIndex !== undefined && !Number.isNaN(analytics.avgHealthIndex)
                      ? analytics.avgHealthIndex.toFixed(1) + '%'
                      : '-'}
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Health Status Distribution</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {(analytics.healthStatusDist || []).length ? (
                      analytics.healthStatusDist.map(([status, count]: any) => (
                        <Chip key={status} size="small" label={`${status}: ${count}`} />
                      ))
                    ) : (
                      <Typography variant="body2">No data</Typography>
                    )}
                  </Box>
                </CardContent></Card>
              </Grid>
              {/* Health Index section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Health Index by Age Group
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(analytics.healthIndexByAge || []).length ? (
                        analytics.healthIndexByAge.map((row: any) => {
                          const avg = row?.avg ?? 0;
                          const pct = Math.max(0, Math.min(100, avg));
                          return (
                            <Box key={row.bucket}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{row.bucket}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {avg ? avg.toFixed(1) + '%' : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
                              </Box>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography variant="body2">No data</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Health Index by Gender
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(analytics.healthIndexByGender || []).length ? (
                        analytics.healthIndexByGender.map((row: any) => {
                          const avg = row?.avg ?? 0;
                          const pct = Math.max(0, Math.min(100, avg));
                          return (
                            <Box key={row.gender}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{row.gender}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {avg ? avg.toFixed(1) + '%' : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'secondary.main' }} />
                              </Box>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography variant="body2">No data</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              {/* Wellness analytics */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Top Wellness Selections
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {analytics.topWellness.length ? (
                        analytics.topWellness.map(([name, count]: any) => (
                          <Chip key={name} label={`${name}: ${count}`} />
                        ))
                      ) : (
                        <Typography variant="body2">No data</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Wellness Selection Pie
                    </Typography>
                    {analytics.wellnessAll && analytics.wellnessAll.length ? (
                      (() => {
                        const palette = ['#1976d2', '#9c27b0', '#ef6c00', '#2e7d32', '#d81b60', '#00838f', '#5d4037', '#0288d1', '#7b1fa2', '#c2185b'];
                        const maxSlices = 8;
                        const top = analytics.wellnessAll.slice(0, maxSlices);
                        const rest = analytics.wellnessAll.slice(maxSlices);
                        const othersCount = rest.reduce((s, [, c]) => s + Number(c), 0);
                        const segments = top.map(([label, value], i) => ({
                          label: String(label),
                          value: Number(value),
                          color: palette[i % palette.length],
                        }));
                        if (othersCount > 0) {
                          segments.push({ label: 'Others', value: othersCount, color: '#bdbdbd' });
                        }
                        return <PieChart data={segments} />;
                      })()
                    ) : (
                      <Typography variant="body2">No data</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              {/* BCA KPI Cards */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Body Composition (BCA)</Typography>
              </Grid>
              {bcaAnalytics.rowsWithBca === 0 ? (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        No BCA data found in the current results. Ensure your records contain a bca_report (or similar) payload.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<BarChartIcon fontSize="small" />} label="Avg BMI" value={bcaAnalytics.avgBmi ? bcaAnalytics.avgBmi.toFixed(1) : '-'} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<FavoriteIcon fontSize="small" />} label="Avg Body Fat %" value={bcaAnalytics.avgBodyFat ? bcaAnalytics.avgBodyFat.toFixed(1) + '%' : '-'} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<FitnessCenterIcon fontSize="small" />} label="Avg Muscle Mass" value={bcaAnalytics.avgMuscle ? bcaAnalytics.avgMuscle.toFixed(1) : '-'} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<OpacityIcon fontSize="small" />} label="Avg Body Water %" value={bcaAnalytics.avgWater ? bcaAnalytics.avgWater.toFixed(1) + '%' : '-'} />
                  </Grid>
                  {/* BCA Distributions */}
                  {[
                    { title: 'BMI Categories', data: bcaAnalytics.bmiDist },
                    { title: 'Body Fat %', data: bcaAnalytics.bodyFatDist },
                    { title: 'Visceral Fat', data: bcaAnalytics.visceralDist },
                    { title: 'Body Water %', data: bcaAnalytics.waterDist },
                  ].map((section) => (
                    <Grid item xs={12} md={6} key={section.title}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>{section.title}</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {section.data.length ? section.data.map(([label, count]) => {
                              const pct = bcaAnalytics.count ? Math.round((Number(count) / bcaAnalytics.count) * 100) : 0;
                              return (
                                <Box key={label}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">{label}</Typography>
                                    <Typography variant="body2" color="text.secondary">{count} ({pct}%)</Typography>
                                  </Box>
                                  <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
                                    <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'secondary.main' }} />
                                  </Box>
                                </Box>
                              );
                            }) : <Typography variant="body2">No data</Typography>}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </>
              )}
              {/* Cohort Distributions */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Cohort Breakdown</Typography>
              </Grid>
              {[
                { title: 'Age Groups', data: cohorts.age },
                { title: 'Gender', data: cohorts.gender },
                { title: 'Blood Pressure Classes', data: cohorts.bp },
                { title: 'RBS (mg/dL)', data: cohorts.rbs },
                { title: 'SpO₂ (%)', data: cohorts.spo2 },
                { title: 'Height (cm)', data: cohorts.height },
              ].map((section) => (
                <Grid item xs={12} md={6} key={section.title}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{section.title}</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {section.data.length ? section.data.map(([label, count]) => {
                          const pct = cohorts.total ? Math.round((Number(count) / cohorts.total) * 100) : 0;
                          return (
                            <Box key={label}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">{label}</Typography>
                                <Typography variant="body2" color="text.secondary">{count} ({pct}%)</Typography>
                              </Box>
                              <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
                              </Box>
                            </Box>
                          );
                        }) : <Typography variant="body2">No data</Typography>}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Columns Picker Dialog */}
      <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Choose Columns</Typography>
            <IconButton aria-label="close" onClick={() => setColumnsDialogOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button size="small" variant="outlined" onClick={() => setVisibleFields(columns.map(c => c.field))} disabled={!columns.length}>Select All</Button>
            <Button size="small" variant="outlined" onClick={() => setVisibleFields([])} disabled={!columns.length}>Clear All</Button>
            <Button size="small" variant="outlined" onClick={() => setVisibleFields(columns.map(c => c.field))} disabled={!columns.length}>Reset</Button>
          </Box>
          <FormGroup>
            {columns.map((c) => (
              <FormControlLabel
                key={c.field}
                control={
                  <Checkbox
                    checked={!!visibleFields?.includes(c.field)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setVisibleFields((prev) => {
                        const cur = new Set(prev || []);
                        if (checked) cur.add(c.field); else cur.delete(c.field);
                        return Array.from(cur);
                      });
                    }}
                  />
                }
                label={c.headerName || c.field}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setColumnsDialogOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={healthDialogOpen}
        onClose={() => setHealthDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Overall Health Index</Typography>
            <IconButton aria-label="close" onClick={() => setHealthDialogOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%' }}>
            {healthDialogHtml && (
              <div dangerouslySetInnerHTML={{ __html: healthDialogHtml }} />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          p: 3,
          background: 'linear-gradient(135deg, #75623f 0%, #3B362A 100%)',
          color: 'white',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Patient Report Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Comprehensive health assessment overview
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setDetailOpen(false)}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          background: 'linear-gradient(to right, #F8F2E3, #F4ECD6)'
        }}>
          <Tabs
            value={detailTab}
            onChange={(_, newValue) => setDetailTab(newValue)}
            aria-label="detail tabs"
            sx={{
              px: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
                '&.Mui-selected': {
                  color: '#75623f',
                  fontWeight: 600
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#75623f',
                height: 3,
                borderRadius: 1.5
              }
            }}
          >
            <Tab label="Profile" value="profile" />
            <Tab label="Device Records" value="devices" />
            <Tab label="Vikriti" value="vikriti" />
            <Tab label="Prakriti" value="prakriti" />
            <Tab label="Wellness" value="wellness" />
            <Tab label="BCA" value="bca" />
            <Tab label="Report" value="report" />
          </Tabs>
        </Box>
        <DialogContent
          dividers
          sx={{
            minHeight: '500px',
            p: 0,
            background: '#FDFDFD'
          }}
        >
          {selected && (
            <Box>
              {detailTab === 'profile' && (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selected.kioskId && <Chip label={`Kiosk: ${selected.kioskId}`} size="small" sx={{ backgroundColor: '#F4ECD6', color: '#75623f', border: '1px solid #C6B897' }} />}
                    {selected.kioskName && <Chip label={selected.kioskName} size="small" sx={{ backgroundColor: '#DBF9DB', color: '#3B362A', border: '1px solid #8CEB8C' }} />}
                    {selected.date && <Chip label={`Date: ${selected.date}`} size="small" sx={{ backgroundColor: '#FBF1E7', color: '#DA6E0B', border: '1px solid #E69E5C' }} />}
                  </Box>

                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="h6" sx={{ color: '#75623f', fontWeight: 600 }}>
                          Personal Information
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            const d = (selected?.data ?? selected?.Data) as any;
                            const profile = (selected?.profileData ?? selected?.ProfileData) as any;
                            const vikriti = d?.vikriti ?? d?.Vikriti;
                            const systolic = d?.systolic;
                            const diastolic = d?.diastolic;
                            const spo2 = d?.spo2;
                            const pulse = d?.pulse;
                            const rbs = d?.rbs;
                            const handgripStrength = d?.handgrip ?? d?.handgripStrength ?? d?.handGripStrength;
                            const healthScore = calculateKioskHealthScore({ vikriti, systolic, diastolic, spo2, pulse, rbs, profile, handgripStrength }, true);
                            const html = generateHealthIndexHTML(healthScore.healthIndex, healthScore.scores);
                            setHealthDialogHtml(html);
                            setHealthDialogOpen(true);
                          }}
                        >
                          View Health Index
                        </Button>
                      </Box>
                      <Grid container spacing={3}>
                        {(() => {
                          const pd = (selected?.profileData ?? selected?.ProfileData) as any;
                          return (
                            <>
                              {(pd?.UID || pd?.uid) && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      USER ID
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                      {pd.UID || pd.uid}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {(pd?.firstname || pd?.firstName || pd?.FirstName) && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      FIRST NAME
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                      {pd.firstname || pd.firstName || pd.FirstName}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {(pd?.height || pd?.Height) && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      HEIGHT
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                      {pd.height || pd.Height}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {(pd?.dob || pd?.DOB || pd?.dateOfBirth) && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      DATE OF BIRTH
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                      {(() => {
                                        const v = pd.dob || pd.DOB || pd.dateOfBirth;
                                        if (!v) return '-';
                                        let d: Date | null = null;
                                        if (typeof v === 'number') d = new Date(v);
                                        else if (typeof v === 'string') {
                                          // Check if it's ddMMyyyy format (8 digits)
                                          if (/^\d{8}$/.test(v)) {
                                            const day = v.substring(0, 2);
                                            const month = v.substring(2, 4);
                                            const year = v.substring(4, 8);

                                            // Handle edge case like 00002002 - just show year
                                            if (day === '00' && month === '00') {
                                              return year;
                                            }

                                            d = new Date(`${year}-${month}-${day}`);
                                          } else {
                                            const num = /^\d+$/.test(v) ? Number(v) : NaN;
                                            d = isNaN(num) ? new Date(v) : new Date(num);
                                          }
                                        } else if (v instanceof Date) d = v;
                                        if (!d || isNaN(d.getTime())) return String(v);
                                        try { return format(d, 'dd MMM yyyy'); } catch { return d.toISOString().slice(0, 10); }
                                      })()}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {(() => {
                                const phone = (
                                  pd?.phone ||
                                  pd?.phoneNumber ||
                                  pd?.mobile ||
                                  pd?.mobileNumber ||
                                  pd?.contactNumber ||
                                  selected?.phone ||
                                  selected?.phoneNumber ||
                                  selected?.mobile ||
                                  ''
                                );
                                return phone ? (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        PHONE NUMBER
                                      </Typography>
                                      <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                        {phone}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ) : null;
                              })()}
                              {(() => {
                                const email = pd?.email || selected?.email || '';
                                return email ? (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        EMAIL ADDRESS
                                      </Typography>
                                      <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                        {email}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ) : null;
                              })()}
                              {(pd?.motherTongue || pd?.MotherTongue || pd?.language) && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      MOTHER TONGUE
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 0.5, color: '#3B362A', fontWeight: 600 }}>
                                      {pd.motherTongue || pd.MotherTongue || pd.language}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </>
                          );
                        })()}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'vikriti' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Vikriti Symptoms
                      </Typography>
                      {(() => {
                        const d = (selected?.data ?? selected?.Data) as any;
                        let vikriti: any = d?.vikriti ?? d?.Vikriti;
                        if (!vikriti) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No vikriti data found for this record.
                            </Typography>
                          );
                        }
                        if (typeof vikriti === 'string') {
                          try {
                            vikriti = JSON.parse(vikriti);
                          } catch {
                            vikriti = {};
                          }
                        }
                        const vikritiList: any[] = vikriti?.vikritiList || vikriti?.VikritiList || [];
                        if (!Array.isArray(vikritiList) || vikritiList.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No symptoms selected in vikriti.
                            </Typography>
                          );
                        }
                        return (
                          <Grid container spacing={2}>
                            {vikritiList.map((item: any, idx: number) => {
                              const name = item?.symptoms ?? item?.symptomName ?? item?.symptom ?? item?.name ?? item?.SymptomName ?? item?.Symptom;
                              const severity = item?.severity ?? item?.Severity ?? item?.score ?? item?.Score;
                              const category = item?.category ?? item?.Category;
                              return (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {name || `Symptom ${idx + 1}`}
                                      </Typography>
                                      {category && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                          Category: {category}
                                        </Typography>
                                      )}
                                      {severity !== undefined && severity !== null && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          Severity: {severity}
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'devices' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Vital Signs & Measurements
                      </Typography>
                      <Grid container spacing={3}>
                        {(selected.data?.spo2 || selected.Data?.spo2) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #75623f20, #C6B89720)',
                              borderRadius: 2,
                              border: '1px solid #F4ECD6',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                SpO₂
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#75623f', fontWeight: 700 }}>
                                {selected.data?.spo2 || selected.Data?.spo2}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.pulse || selected.Data?.pulse) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #8CEB8C20, #DBF9DB20)',
                              borderRadius: 2,
                              border: '1px solid #DBF9DB',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                PULSE
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#8CEB8C', fontWeight: 700 }}>
                                {selected.data?.pulse || selected.Data?.pulse}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.systolic || selected.Data?.systolic) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #DA6E0B20, #E69E5C20)',
                              borderRadius: 2,
                              border: '1px solid #FBF1E7',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                SYSTOLIC BP
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#DA6E0B', fontWeight: 700 }}>
                                {selected.data?.systolic || selected.Data?.systolic}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.diastolic || selected.Data?.diastolic) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #75623f20, #3B362A20)',
                              borderRadius: 2,
                              border: '1px solid #C6B897',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                DIASTOLIC BP
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#3B362A', fontWeight: 700 }}>
                                {selected.data?.diastolic || selected.Data?.diastolic}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.rbs || selected.Data?.rbs) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #8CEB8C20, #DBF9DB20)',
                              borderRadius: 2,
                              border: '1px solid #DBF9DB',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                RBS
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#8CEB8C', fontWeight: 700 }}>
                                {selected.data?.rbs || selected.Data?.rbs}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.handgrip || selected.data?.handgripStrength || selected.data?.handGripStrength ||
                          selected.Data?.handgrip || selected.Data?.handgripStrength || selected.Data?.handGripStrength) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #3B82F620, #BFDBFE20)',
                              borderRadius: 2,
                              border: '1px solid #BFDBFE',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                HANDGRIP STRENGTH
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#1D4ED8', fontWeight: 700 }}>
                                {selected.data?.handgrip || selected.data?.handgripStrength || selected.data?.handGripStrength ||
                                  selected.Data?.handgrip || selected.Data?.handgripStrength || selected.Data?.handGripStrength}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selected.data?.temperature || selected.Data?.temperature) && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, #DA6E0B20, #E69E5C20)',
                              borderRadius: 2,
                              border: '1px solid #FBF1E7',
                              textAlign: 'center'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                TEMPERATURE
                              </Typography>
                              <Typography variant="h4" sx={{ mt: 1, color: '#DA6E0B', fontWeight: 700 }}>
                                {selected.data?.temperature || selected.Data?.temperature}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'prakriti' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Prakriti Analysis
                      </Typography>
                      {renderPrettyPrakriti(selected.data?.prakriti ?? selected.Data?.prakriti)}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'wellness' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Wellness Assessment
                      </Typography>
                      {renderPrettyWellness(selected.data?.Wellness ?? selected.Data?.Wellness)}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'bca' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Body Composition Analysis
                      </Typography>
                      {renderPrettyBca(selected.data?.bca_report ?? selected.Data?.bca_report)}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {detailTab === 'report' && (
                <Box sx={{ p: 3 }}>
                  <Card sx={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, color: '#75623f', fontWeight: 600 }}>
                        Generated Report
                      </Typography>
                      {(() => {
                        const reportUrl = selected.data?.Report ?? selected.Data?.Report;
                        if (reportUrl) {
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Button
                                size="large"
                                variant="contained"
                                onClick={() => window.open(reportUrl, '_blank')}
                                sx={{
                                  background: 'linear-gradient(135deg, #75623f, #3B362A)',
                                  '&:hover': { background: 'linear-gradient(135deg, #8a7049, #4a3f32)' }
                                }}
                              >
                                Open Report in New Tab
                              </Button>
                              {reportUrl.toLowerCase().includes('.pdf') ? (
                                <Box sx={{
                                  border: '2px solid #C6B897',
                                  borderRadius: 2,
                                  height: 520,
                                  overflow: 'hidden'
                                }}>
                                  <iframe title="Report" src={reportUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                                </Box>
                              ) : (
                                <Box sx={{ p: 2, backgroundColor: '#F8F2E3', borderRadius: 1, border: '1px solid #C6B897' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Report link: <a href={reportUrl} target="_blank" rel="noreferrer" style={{ color: '#75623f' }}>{reportUrl}</a>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        } else {
                          return (
                            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#F8F2E3', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary">No report available for this record.</Typography>
                            </Box>
                          );
                        }
                      })()}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined" sx={{ color: '#75623f', borderColor: '#75623f', '&:hover': { backgroundColor: '#F8F2E3', borderColor: '#3B362A' } }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bcaOpen} onClose={() => setBcaOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">BCA Report</Typography>
            <IconButton aria-label="close" onClick={() => setBcaOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderPrettyBca(bcaData)}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBcaOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={prakritiOpen} onClose={() => setPrakritiOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Prakriti</Typography>
            <IconButton aria-label="close" onClick={() => setPrakritiOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderPrettyPrakriti(prakritiData)}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPrakritiOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Report</Typography>
            <IconButton aria-label="close" onClick={() => setReportOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {reportUrl ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button size="small" variant="contained" onClick={() => window.open(reportUrl!, '_blank')}>Open in new tab</Button>
              {reportUrl.toLowerCase().includes('.pdf') ? (
                <Box sx={{ border: '1px solid', borderColor: 'divider', height: 520 }}>
                  <iframe title="Report" src={reportUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                </Box>
              ) : (
                <Typography variant="body2">Report link: <a href={reportUrl} target="_blank" rel="noreferrer">{reportUrl}</a></Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2">No report link found.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReportOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={wellnessOpen} onClose={() => setWellnessOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Wellness</Typography>
            <IconButton aria-label="close" onClick={() => setWellnessOpen(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderPrettyWellness(wellnessData)}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWellnessOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KioskReportsPage;
